import { fashionModelAcceptControlTerms } from "@/helpers/utils/accept-control-constants";
import { zodHeight, zodMeasurement, zodShoeSize } from "@/helpers/utils/measurements";
import {
  AreaOfInterest,
  ERROR_MESSAGES,
  activityAreasOptions,
  ageOptions,
  areaOfInterestOptionsWithNote,
  audienceOptions,
  availabilityOptions,
  b2bPrimaryGoalOptions,
  b2bSessionOptions,
  businessTypeOptions,
  companyCategoryOptions,
  companyInBusinessOptions,
  companySizeOptions,
  contributeToEnvironmentOptions,
  defaultRadioOptions,
  fashionExhibitPackageOptions,
  fashionExhibitEventOptions,
  fashionExhibitCategoryOptions,
  fashionExhibitPriceRangeOptions,
  fashionExhibitResaleOptions,
  fashionExhibitDropOptions,
  fashionExhibitTypeOptions,
  fashionModelAvailableDays,
  fashionPackageOptions,
  fashionPackageNotes,
  modelShoeSizeTypeOptions,
  fashionPartnerGoalOptions,
  fashionPartnerTierOptions,
  fashionSelectionCategoryOptions,
  formSlugs,
  fundFocusOptions,
  gamingPitchQuestPlatformOptions,
  gamingPitchQuestTeamOptions,
  genderOptions,
  generalSpeakerTopicCategoryOptions,
  industryOptions,
  investingExperienceOptions,
  investmentStageOptions,
  musicPubisherGenreOptions,
  musicPublisherActiveYearsOptions,
  musicPublisherCollaborationTypeOptions,
  musicPublisherPubisherSoughtTypeOptions,
  newVendorOptions,
  numberOfLooksOptions,
  organizationTypeOptions,
  raceOptions,
  speakerTopicCategoryOptions,
  vendorAcknowledgementOptions,
  ventureDurationOptions,
  zodActivityAreasOptionsSchema,
  zodRaceOptionsSchema,
  filmGenreOptions,
  artFormOptions,
  partnershipTierOptions,
  healthPracticeAreaOptions,
  photographerFebArrangementOptions,
  ctjamExperienceOptions,
  ctjamJamOptions,
  ctjamRoleOptions,
  ctjamTeamOptions,
  photographerFebAvailabilityOptions,
  photographerFebCoverageOptions,
  photographerFebRightsOptions,
  videographerMediumOptions,
  videographerContentStyleOptions,
} from "@/helpers/utils/form-constants";
import {
  zodString,
  zodFileValidator,
  zodEmailValidator,
  zodPhoneNumberValidator,
  createIntroControl,
  createTextControl,
  createAcceptControl,
  createCheckboxControl,
  createCheckboxGroupControl,
  createFileControl,
  createRadioControl,
  createTextareaControl,
  zodAcceptTerms,
  createCheckboxWithNoteControl,
  zodCheckbox,
  zodCheckboxGroup,
  zodCheckboxWithNote,
  zodPhone,
  zodRadio,
  zodTextarea,
  zodUrl,
  createEmailControl,
  createPhoneControl,
  createUrlControl,
} from "@/helpers/utils/utility-functions";
import {
  FileOptions,
  FormControl,
  InputGroupType,
  OptionWithNote,
  TextFormOptions,
  TextLengthCheckOptionType,
} from "@/models/form-types";
import { z, ZodRawShape } from "zod";
import { villageInterestControls, villageInterestSchema, villageVendorControls, villageVendorSchema } from "./village-forms";
import { resumeReviewConsent } from "@/helpers/utils/village-intake";
import { audienceReachControls, audienceReachSchema, withAudienceReach } from "./audience-reach";
import { formNames } from "./form-names";
import { filmIndustryControls, filmIndustrySchema, filmPitchControls, filmPitchSchema, filmSubmissionControls, filmSubmissionSchema } from "./film-forms";
import { startupAlphaControls, startupAlphaSchemaOverrides } from "./startup-alpha-form";

type FormControls = {
  [key: string]: {
    [nestedKey: string]: FormControl;
  };
};

const createZodSchema = (
  controls: Record<string, FormControl>
): ZodRawShape => {
  const schema: ZodRawShape = {};

  Object.keys(controls).forEach((key) => {
    const control = controls[key];
    let fieldSchema: z.ZodTypeAny;

    switch (control.type) {
      case "text":
        fieldSchema = zodString(
          control.label,
          control.isOptional,
          control.minLength,
          control.maxLength,
          (control.options as TextFormOptions)?.lengthCheckType
        );
        break;
      case "textarea":
        fieldSchema = zodTextarea(
          control.label,
          control.isOptional,
          control.minLength,
          control.maxLength,
          (control.options as TextFormOptions)?.lengthCheckType
        );
        break;
      case "phone":
        fieldSchema = zodPhone(control.label, control.isOptional);
        break;
      case "checkbox":
        if (control.options && (control.options as string[]).length > 0) {
          fieldSchema = zodCheckbox(
            control.label,
            control.options as string[],
            control.isOptional
          );
        } else {
          fieldSchema = z
            .array(z.string())
            .min(1, { message: ERROR_MESSAGES.FieldRequired(control.label) });
        }
        break;
      case "checkbox-group":
        if (
          control.options &&
          (control.options as InputGroupType[]).length > 0
        ) {
          fieldSchema = zodCheckboxGroup(
            control.label,
            control.options as InputGroupType[],
            control.isOptional
          );
        } else {
          fieldSchema = z
            .array(z.string())
            .min(1, { message: ERROR_MESSAGES.SelectOne() });
        }
        break;
      case "checkbox-with-note":
        if (
          control.options &&
          (control.options as OptionWithNote<string>[]).length > 0
        ) {
          fieldSchema = zodCheckboxWithNote(
            control.label,
            control.options as OptionWithNote<string>[],
            control.isOptional
          );
        } else {
          fieldSchema = z
            .array(z.string())
            .min(1, { message: ERROR_MESSAGES.SelectOne() });
        }
        break;
      case "radio":
        if (control.options && (control.options as string[]).length > 0) {
          fieldSchema = zodRadio(
            control.label,
            control.options as string[],
            control.isOptional
          );
        } else {
          fieldSchema = z
            .string()
            .min(1, { message: ERROR_MESSAGES.SelectOne() });
        }
        break;
      case "file":
        const fileOptions = control.options as FileOptions;
        fieldSchema = zodFileValidator(
          control.label,
          fileOptions.allowedFileExtentions,
          fileOptions.maxFileSizeMB
        );
        if (control.isOptional) {
          fieldSchema = fieldSchema.optional();
        }
        break;

      case "url":
        fieldSchema = zodUrl(control.label, control.isOptional);
        break;

      case "email":
        fieldSchema = zodEmailValidator(control.label, control.isOptional);
        break;
      case "accept":
        fieldSchema = z.string().refine((val) => val === "Yes", {
          message: ERROR_MESSAGES.AcceptTerms(),
        });
        break;

      case "intro":
      default:
        fieldSchema = z.string().optional();
        break;
    }

    schema[key] = fieldSchema;
  });

  if (schema.intro) delete schema.intro;
  return schema;
};

const musicPublisherFormControls: { [x: string]: FormControl } = {
  intro: createIntroControl(
    "C-Tribe x Music Publisher’s Zone Registration Form 2027 - For Artists, Musicians, Performers, Songwriters, and Bands",
    [
      "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams.",
      "We're looking for music industry professionals and publishers looking to build contacts, expose the latest developments in their music, get real-time feedback from indsutry, and connect with the music industry.",
    ]
  ),
  firstName: createTextControl("What is your first name?"),
  lastName: createTextControl("What is your last name?"),
  stageName: createTextControl("What is your stage name/brand name?"),
  email: createEmailControl("What email address can we reach you at?"),
  phoneNumber: createPhoneControl("What's the best phone number to reach you?"),
  bio: createTextareaControl(
    "Please share a bio of yourself and your band",
    "( 250 words max )",
    false,
    1,
    250,
    TextLengthCheckOptionType.WORD_COUNT
  ),
  photo: createFileControl(
    "Please upload a high-resolution image of yourself or your band  (500 px by 500 px or higher)",
    "",
    2,
    ["jpg", "jpeg", "png"]
  ),
  genre: createCheckboxControl(
    "Please select Genres Represented",
    musicPubisherGenreOptions,
    "Select all that apply"
  ),
  otherGenre: createTextControl(
    "If you selected other, please specify",
    "",
    true
  ),
  activeYearsInIndustry: createRadioControl(
    "Year’s active in the industry",
    musicPublisherActiveYearsOptions
  ),
  yourInspiration: createTextControl(
    "Please share any artists or bands that influence/inspire you and your work",
    ""
  ),
  notablePerformances: createTextControl(
    "Please share any notable performances or collaborations",
    "( links if available )"
  ),
  notableAwards: createTextControl(
    "Please share any notable awards or recognition",
    "( links if available )"
  ),
  upcomingProjects: createTextareaControl(
    "Tell us about any current project(s) or upcoming releases"
  ),
  prefarableCollaborationType: createCheckboxControl(
    "Preferred collaboration type",
    musicPublisherCollaborationTypeOptions,
    "Select all that apply"
  ),
  publisherAttributes: createTextControl(
    "What are your Ideal Publisher Attributes",
    "( e.g., genre specialization, location, size of publisher )"
  ),
  links: createTextControl(
    "Please share any social media links to you and your work"
  ),
  diversityForYou: createTextareaControl(
    "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
    "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
  ),
  acceptTerms: createAcceptControl(),
};

const musicPublisherPublisherFromControls: { [x: string]: FormControl } = {
  intro: createIntroControl(
    "C-Tribe x Music Publisher’s Zone Registration Form 2027 - For Publishers",
    [
      "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams.",
      "We're looking for music industry professionals and publishers looking to build contacts, expose the latest developments in their music, get real-time feedback from industry, and connect with the music industry.",
    ]
  ),
  firstName: createTextControl("What is your first name?"),
  lastName: createTextControl("What is your last name?"),
  companyName: createTextControl("What is your company name?"),
  companyWebsite: createUrlControl("What is your company website?"),
  email: createEmailControl("What email address can we reach you at?"),
  phoneNumber: createPhoneControl("What's the best phone number to reach you?"),
  bio: createTextareaControl(
    "Please share a bio of yourself and your company",
    "( 250 words max )",
    false,
    1,
    250,
    TextLengthCheckOptionType.WORD_COUNT
  ),
  photo: createFileControl(
    "Please upload a high-resolution image of yourself or your company logo (500 px by 500 px or higher)",
    "",
    2,
    ["jpg", "jpeg", "png"]
  ),
  genre: createCheckboxControl(
    "Please select Genres Represented",
    musicPubisherGenreOptions,
    "Select all that apply"
  ),
  otherGenre: createTextControl(
    "If you selected other, please specify",
    "",
    true
  ),
  activeYearsInIndustry: createRadioControl(
    "Year’s active in the industry",
    musicPublisherActiveYearsOptions
  ),
  representedArtists: createTextControl(
    "Please share any notable artists or bands represented",
    "( links if available )"
  ),
  notableSongs: createTextControl(
    "Please share any notable songs in catalogue",
    "( links if available )"
  ),
  notableAwards: createTextControl(
    "Please share any notable awards or recognition",
    "( links if available )"
  ),
  upcomingProjects: createTextareaControl(
    "Tell us about any current project(s) or upcoming releases"
  ),
  achievements: createTextareaControl(
    "Please share any major achievements",
    "( e.g., chart-topping hits, award-winning songs etc. )"
  ),
  soughtType: createCheckboxControl(
    "Type of Talent Sought",
    musicPublisherPubisherSoughtTypeOptions,
    "Select all that apply"
  ),
  otherSoughtType: createTextControl(
    "If you selected other, please specify",
    "",
    true
  ),
  prefarableCollaborationType: createCheckboxControl(
    "Preferred collaboration type",
    musicPublisherCollaborationTypeOptions,
    "Select all that apply"
  ),
  publisherAttributes: createTextControl(
    "What are your Ideal Publisher Attributes",
    "( e.g., genre specialization, location, size of publisher )"
  ),
  links: createTextControl(
    "Please share any social media links to you and your work"
  ),
  diversityForYou: createTextareaControl(
    "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
    "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
  ),
  acceptTerms: createAcceptControl(),
};

const techExecLuncheonFormControls: { [x: string]: FormControl } = {
  intro: createIntroControl("C-Tribe Tech Exec Luncheon Registration", [
    "At the C-Tribe x Tech Exec Luncheon, we invite you to forge powerful connections with like-minded industry leaders, enabling you to drive the evolution of technology forward and create lasting impact. Together, we'll navigate the ever-changing tech landscape, explore emerging opportunities, and set the stage for a brighter future.",
  ]),
  firstName: createTextControl("What is your first name?"),
  lastName: createTextControl("What is your last name?"),
  email: createEmailControl("What email address can we reach you at?"),
};

const diversityPassFormControls: { [x: string]: FormControl } = {
  intro: createIntroControl("C-Tribe Diversity Pass Registration", [
    "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams.",
    "We set aside a portion of delegate passes that are provided for free to individuals who embrace diversity and/or may not be able to afford the full ticket price. Due to the allotment of Diversity Passes we have available, recipients will be awarded based on their successful application.",
  ]),

  firstName: createTextControl("What is your first name?", ""),
  lastName: createTextControl("What is your last name?", ""),
  email: createEmailControl("What email address can we reach you at?", ""),
  phone: createPhoneControl("What's the best phone number to reach you?", ""),
  company: createTextControl("What is your company name?"),
  jobTitle: createTextControl("What is your job title?"),
  primaryIndustry: createRadioControl(
    "What is your primary industry?",
    industryOptions,
    ""
  ),
  secondaryIndustry: createCheckboxControl(
    "What is your secondary industry?",
    industryOptions,
    ""
  ),
  portfolioWebsite: createUrlControl(
    "Please share your portfolio website",
    "( optional )",
    true
  ),
  ageRange: createRadioControl("What is your age range?", ageOptions, ""),
  race: createCheckboxGroupControl(
    "How Would You Describe Your Race?",
    raceOptions,
    "Select all that apply"
  ),
  gender: createCheckboxControl(
    "What is your gender identity?",
    genderOptions,
    "Select all that apply",
    false
  ),
  linkedin: createUrlControl(
    "Please share your LinkedIn profile",
    "( optional )",
    true
  ),
  instagram: createUrlControl(
    "Please share your Instagram profile",
    "( optional )",
    true
  ),
  twitter: createUrlControl(
    "Please share your Twitter profile",
    "( optional )",
    true
  ),
  tiktok: createUrlControl(
    "Please share your TikTok profile",
    "( optional )",
    true
  ),
};

const photographerFormControls: { [x: string]: FormControl } = {
  intro: createIntroControl(
    "C-Tribe Photographer & Videographer Registration Form 2027",
    [
      "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams.",
      "We're looking for photographers to take part in the festival & conference. If you’re an emerging photographer and videographer and want to be part of a one-of-a-kind festival experience, this is for you. Each successful applicant will be provided with an all-access badge to explore the full festival offerings.",
    ]
  ),

  firstName: createTextControl("What is your first name?", ""),
  lastName: createTextControl("What is your last name?", ""),
  portfolioWebsite: createUrlControl(
    "Please share the URL to your portfolio or social media handle with your most relevant and best work",
    ""
  ),
  primaryContent: createTextControl(
    "What is your primary content focus?",
    "(e.g., fashion, travel, food, technology, music etc.)"
  ),
  email: createEmailControl("What email address can we reach you at?", ""),
  phone: createPhoneControl("What's the best phone number to reach you?", ""),
  aimToAchieve: createTextareaControl(
    "What do you aim to achieve by participating as a photographer or videographer at this event?"
  ),
  diversityMeaning: createTextareaControl(
    "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
    "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
  ),
  acceptTerms: createAcceptControl(),
};

const musicArtistFormControls: { [x: string]: FormControl } = {
  firstName: createTextControl("What is your first name?"),
  lastName: createTextControl("What is your last name?"),
  artistName: createTextControl("What is your artist name?"),
  socialMediaHandle: createUrlControl("What is your social media handle?"),
  email: createEmailControl("What is your email address?"),
  phoneNumber: createPhoneControl("What is your phone number?"),
  links: createTextControl(
    "Please share any links to your music?",
    "( optional )",
    true
  ),
  acceptTerms: createAcceptControl(),
};

const generalSpeakerFormControls: { [x: string]: FormControl } = {
  intro: createIntroControl("Apply as a speaker for C-Tribe Festival 2027", [
    "The C-Tribe Festival is Canada's eminent arts, technology, and culture gathering. We aim to celebrate diversity and help more innovative and creative people achieve their dreams. We curate talks, workshops, panels & masterclasses that deep dive into specific topics across a variety of industries.",
    "If you are interested in having the opportunity to speak as a keynote speaker at C-Tribe 2027, please fill out the form.",
  ]),
  firstName: createTextControl("What is your first name?"),
  lastName: createTextControl("What is your last name?"),
  companyName: createTextControl("What is your company name?"),
  jobTitle: createTextControl("What is your job title?"),
  photo: createFileControl(
    "Please upload a high-resolution photo of yourself (500 px by 500 px or higher)",
    "",
    2,
    ["jpg", "png"]
  ),
  email: createEmailControl("What email address can we reach you at?"),
  phone: createPhoneControl("What's the best phone number to reach you?"),
  speakerBio: createTextareaControl(
    "Speaker bio (250 words max)",
    "",
    false,
    1,
    250,
    TextLengthCheckOptionType.WORD_COUNT
  ),
  speakerTopicCategory: createRadioControl(
    "Which category does your speaker topic fit in?",
    generalSpeakerTopicCategoryOptions
  ),
  sessionTitle: createTextControl(
    "Proposed session title (10 words max)",
    "",
    false,
    1,
    10,
    TextLengthCheckOptionType.WORD_COUNT
  ),
  links: createTextControl(
    "Please share any links to some recent speaking engagements you have been a part of. Links to video or audio are preferred",
    "( optional )",
    true
  ),
  acceptTerms: createAcceptControl(),
};

const cBlackTechPitchSemiFinalsFormControls: { [x: string]: FormControl } = {
  intro: createIntroControl(
    "Congrats on making it as a C-Black x Tech Pitch Semi-Finalist!",
    [
      "We're excited about the possibility to feature your company this year during the event. Please fill out the form to confirm your participation.",
    ]
  ),
  firstName: createTextControl("Let's start with your first name."),
  lastName: createTextControl("And your last name?"),
  isAbleToParticipate: createRadioControl(
    "If you're selected as a finalist, are you able to be in Edmonton during C-Tribe Festival, August 12-17, 2027, to participate in the event?",
    defaultRadioOptions,
    "Please note that only finalists who can be in person in Edmonton are eligible to pitch in the competition."
  ),
  startUpPitchUrl: createUrlControl(
    "Please provide a 4 minute pitch demo of you and/or your team pitching your startup. Kindly upload it and please share the address.",
    "You can upload it as an unlisted video on YouTube, Vimeo etc. but please do not password-protect it. Kindly note the criteria that the judging committee will evaluate pitches by: a) Identification of a problem and the effectiveness of its proposed solution (20%) b) Target market size and positioning against competitors (30%) c) Strategies for customer acquisition, distribution, and revenue generation (20%) d)Team quality (20%) e) And overall pitch clarity, entertainment factor, and persuasiveness (10%)"
  ),
  email: createEmailControl(
    "Please confirm the email address you originally submitted your application with."
  ),
  acceptTerms: createAcceptControl(),
};

const cElevateHerTechPitchSemiFinalsFormControls: { [x: string]: FormControl } =
  {
    intro: createIntroControl(
      "Congrats on making it as a C-ElevateHer x Tech Pitch Semi-Finalist!",
      [
        "We're excited about the possibility to feature your company this year during the event. Please fill out the form to confirm your participation.",
      ]
    ),
    firstName: createTextControl("Let's start with your first name."),
    lastName: createTextControl("And your last name?"),
    isAbleToParticipate: createRadioControl(
      "If you're selected as a finalist, are you able to be in Edmonton during C-Tribe Festival, August 12-17, 2027, to participate in the event?",
      defaultRadioOptions,
      "Please note that only finalists who can be in person in Edmonton are eligible to pitch in the competition."
    ),
    startUpPitchUrl: createUrlControl(
      "Please provide a 4 minute pitch demo of you and/or your team pitching your startup. Kindly upload it and please share the address.",
      "You can upload it as an unlisted video on YouTube, Vimeo etc. but please do not password-protect it. Kindly note the criteria that the judging committee will evaluate pitches by: a) Identification of a problem and the effectiveness of its proposed solution (20%) b) Target market size and positioning against competitors (30%) c) Strategies for customer acquisition, distribution, and revenue generation (20%) d)Team quality (20%) e) And overall pitch clarity, entertainment factor, and persuasiveness (10%)"
    ),
    email: createEmailControl(
      "Please confirm the email address you originally submitted your application with."
    ),
    acceptTerms: createAcceptControl(),
  };

const vipDinnerFormControls: { [x: string]: FormControl } = {
  intro: createIntroControl(
    "Attendance Confirmation and Dietary Restrictions for the C-Tribe Supper Club [VIP Dinner]",
    []
  ),
  firstName: createTextControl("Let's start with your first name."),
  lastName: createTextControl("And your last name?"),
  email: createEmailControl(
    "What email address can we reach you at? This is only to get in touch, not to send spam."
  ),
  companyName: createTextControl("What's your company or organisation name?"),
  dietaryRestrictions: createTextControl(
    "Do you have any dietary restrictions or allergies that we should know about",
    "",
    true
  ),
};

const hotelBookingsFormControls: { [x: string]: FormControl } = {
  hotel: createTextControl("Hotel name"),
  startDate: createTextControl("Check-in date"),
  endDate: createTextControl("Check-out date"),
  bedType: createTextControl("Bed type"),
  numAdults: createTextControl("Number of adults"),
  numChildren: createTextControl("Number of children"),
  numRooms: createTextControl("Number of rooms"),
};

const marketingSitePopUpsFormControls: { [x: string]: FormControl } = {
  email: createTextControl("What email address can we reach you at?"),
  interests: createTextControl(
    "Which C-Tribe verticals are you most interested in?"
  ),
};

const innovationZoneFormControls: { [x: string]: FormControl } = {
  intro: createIntroControl("C-Tribe Innovation Zone Registration", []),
  firstName: createTextControl("What is your first name?"),
  lastName: createTextControl("What is your last name?"),
  email: createEmailControl("What email address can we reach you at?"),
};

const formControls: FormControls = {
  [formSlugs.techPitchElevateHer]: {
    intro: createIntroControl("Register for the C-ElevateHer Pitch 2027", [
      "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams. The Elevate program seeks to strengthen pathways to tech-enabled entrepreneurship, increase access to capital and expand pathways to growth and highlight the talents of underserved founders",
      "Eligible ventures must have raised no more than $1M in dilutive funding to date, and have a Woman Founder with more than 10% controlling equity in the company. This category is for projects that have shown progress against the venture concept (tested the concept, working prototype, minimal viable product etc.)",
    ]),
    companyName: createTextControl("What is your company name?"),
    jobTitle: createTextControl("What is your Job Title?"),
    race: createCheckboxGroupControl(
      "How Would You Describe Your Race?",
      raceOptions,
      "Select all that apply"
    ),
    isIdentifyWomanAndHaveGraterControlingEquity: createRadioControl(
      "Do you or someone on your co-founding team self-identify as a Woman AND have 33% or greater controlling equity (voting shares), a board seat, and is the president/CEO of the company you're applying for?",
      defaultRadioOptions
    ),
    gender: createCheckboxControl(
      "How would you describe your gender?",
      genderOptions,
      "Select all that apply"
    ),
    isCompanyInCanada: createRadioControl(
      "Is your company incorporated in and legally operating in Canada ?",
      defaultRadioOptions,
      "You do not need to be a Canadian entity to participate in the C-Black x Tech Pitch Competition."
    ),
    headquartersCity: createTextControl(
      "Which city is your headquarter location ?"
    ),
    headquartersProvinceState: createTextControl(
      "Which province/state is your headquarter located in ?"
    ),
    headquartersCountry: createTextControl(
      "Which country is your headquarters located in ?"
    ),
    isForProfit: createRadioControl(
      "Is your venture a for-profit?",
      defaultRadioOptions
    ),
    mainIndustry: createRadioControl(
      "What is your main industry?",
      industryOptions
    ),
    additionalIndustries: createCheckboxControl(
      "Which additional industries does your venture address?",
      industryOptions,
      "Please select a maximum of three options."
    ),
    otherIndustry: createTextControl("If you selected Other, please specify?"),
    businessVentureDescription: createTextareaControl(
      "Describe your business venture in 2-3 sentences"
    ),
    teamUniqueness: createTextareaControl(
      "Why is your team uniquely suited to build this company?"
    ),
    problemBeingSolved: createTextareaControl("What problem are you solving?"),
    solutionDescription: createTextareaControl("How does your solution work?"),
    targetedMarketSize: createTextareaControl(
      "How big is the targeted market you’re going after?",
      "Please share any resources or verify with a bottoms up approach outlining your market size."
    ),
    ventureDuration: createRadioControl(
      "How long have you been working on this venture?",
      ventureDurationOptions
    ),
    websiteURL: createTextControl(
      "If you have a website for your business, please share the address",
      "( optional )"
    ),
    videoURL: createTextControl(
      "If you have a video about your business, please share the address"
    ),
    pitchDeckUpload: createFileControl(
      "Please upload your company’s pitch deck",
      "",
      5,
      ["pdf", "doc"]
    ),
    relatesToMissionStatement: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all gender, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.techPitchCBlack]: {
    intro: createIntroControl("Register for the C-Black Pitch 2027", [
      "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams. The Elevate program seeks to strengthen pathways to tech-enabled entrepreneurship, increase access to capital and expand pathways to growth and highlight the talents of underserved founders.",
      "Eligible ventures must have raised no more than $1M in dilutive funding to date and have a founder who identifies as Black, African, or Caribbean holding a majority controlling stake in the company. This category is for projects that have shown progress against the venture concept (tested the concept, working prototype, minimal viable product etc.)",
    ]),
    companyName: createTextControl("What is your company name?"),
    jobTitle: createTextControl("What is your Job Title?"),
    race: createCheckboxGroupControl(
      "How Would You Describe Your Race?",
      raceOptions,
      "Select all that apply"
    ),
    isIdentifyBlackAndHaveGraterControlingEquity: createRadioControl(
      "Does a founder who identifies as Black, African, or Caribbean own a majority controlling stake (more than 50% of voting shares) in the company you're applying for?",
      defaultRadioOptions
    ),
    gender: createCheckboxControl(
      "How would you describe your gender?",
      genderOptions,
      "Select all that apply"
    ),
    isCompanyInCanada: createRadioControl(
      "Is your company incorporated in and legally operating in Canada ?",
      defaultRadioOptions,
      "You do not need to be a Canadian entity to participate in the C-Black x Tech Pitch Competition."
    ),
    headquartersCity: createTextControl(
      "Which city is your headquarter location ?"
    ),
    headquartersProvinceState: createTextControl(
      "Which province/state is your headquarter located in ?"
    ),
    headquartersCountry: createTextControl(
      "Which country is your headquarters located in ?"
    ),
    isForProfit: createRadioControl(
      "Is your venture a for-profit?",
      defaultRadioOptions
    ),
    mainIndustry: createRadioControl(
      "What is your main industry?",
      industryOptions
    ),
    additionalIndustries: createCheckboxControl(
      "Which additional industries does your venture address?",
      industryOptions,
      "Please select a maximum of three options."
    ),
    otherIndustry: createTextControl("If you selected Other, please specify?"),
    businessVentureDescription: createTextareaControl(
      "Describe your business venture in 2-3 sentences"
    ),
    teamUniqueness: createTextareaControl(
      "Why is your team uniquely suited to build this company?"
    ),
    problemBeingSolved: createTextareaControl("What problem are you solving?"),
    solutionDescription: createTextareaControl("How does your solution work?"),
    targetedMarketSize: createTextareaControl(
      "How big is the targeted market you’re going after?",
      "Please share any resources or verify with a bottoms up approach outlining your market size."
    ),
    ventureDuration: createRadioControl(
      "How long have you been working on this venture?",
      ventureDurationOptions
    ),
    websiteURL: createTextControl(
      "If you have a website for your business, please share the address",
      "( optional )"
    ),
    videoURL: createTextControl(
      "If you have a video about your business, please share the address"
    ),
    pitchDeckUpload: createFileControl(
      "Please upload your company’s pitch deck",
      "",
      5,
      ["pdf", "doc"]
    ),
    relatesToMissionStatement: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all gender, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.techElevate]: {
    // intro: createIntroControl(
    //   "Register for the C-Tribe x Tech Elevate Program 2024.",
    //   [
    //     "The C-Tribe Festival aims to celebrate diversity and help innovative and creative people achieve their dreams. The Elevate program seeks to strengthen pathways to tech-enabled entrepreneurship, increase access to capital and expand pathways to growth and highlight the talents of founders",
    //     "Join our growing network of high-growth, tech entrepreneurs. Chosen individuals and companies will receive two C-Tribe x Tech Passes, access to the Investor’s Zone, eligibility to participate in pitch competitions, access to the workshops & masterclasses and more!",
    //   ]
    // ),
    intro: createIntroControl(
      "Register for the C-Tribe x Tech Investor's Zone",
      [
        "Sign-up now to take part in some curated one-on-one sessions with investors. During the sessions you'll have an opportunity to discuss fundraising challenges, strategies and direction.",
        
      ]
    ),
    companyName: createTextControl("What is your company name?"),
    jobTitle: createTextControl("What is your Job Title?"),
    isCompanyInCanada: createRadioControl(
      "Is your company incorporated in and legally operating in Canada?",
      defaultRadioOptions,
      // "Note: You do not need to be a Canadian entity to participate in the C-Tribe x Tech Elevate Program."
       "Note: You do not need to be a Canadian entity to participate in the C-Tribe x Tech  Program."
    ),
    headquartersCity: createTextControl(
      "Which city is your headquarter location?"
    ),
    headquartersProvinceState: createTextControl(
      "Which province/state is your headquarter located in?"
    ),
    headquartersCountry: createTextControl(
      "Which country is your headquarters located in?"
    ),
    isForProfit: createRadioControl(
      "Is your venture a for-profit?",
      defaultRadioOptions
    ),
    mainIndustry: createRadioControl(
      "What is your main industry?",
      industryOptions
    ),
    additionalIndustries: createCheckboxControl(
      "Which additional industries does your venture address?",
      industryOptions,
      "Please select a maximum of three options."
    ),
    otherIndustry: createTextControl("If you selected Other, please specify"),
    businessVentureDescription: createTextControl(
      "Describe your business venture in 2-3 sentences"
    ),
    teamUniqueness: createTextareaControl(
      "Why is your team uniquely suited to build this company?"
    ),
    problemBeingSolved: createTextareaControl("What problem are you solving?"),
    solutionDescription: createTextareaControl("How does your solution work?"),
    targetedMarketSize: createTextareaControl(
      "How big is the targeted market you’re going after?",
      "Please share any resources or verify with a bottoms up approach outlining your market size."
    ),
    ventureDuration: createRadioControl(
      "How long have you been working on this venture?",
      ventureDurationOptions
    ),
    websiteURL: createTextControl(
      "If you have a website for your business, please share the address",
      "(optional)"
    ),
    pitchDeckUpload: createFileControl(
      "Please upload your company’s pitch deck",
      "",
      5,
      ["pdf"]
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.techSpeaker]: {
    intro: createIntroControl("Apply as a speaker for C-Tribe x Tech 2027", [
      "The C-Tribe Festival aims to celebrate diversity and help innovative and creative people achieve their dreams. We curate deep dives into specific predetermined topics.",
      "We're looking for speakers who align with that goal of providing useful insight and advice.",
    ]),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    companyName: createTextControl("What is your company name?"),
    jobTitle: createTextControl("What is your job title?"),
    photo: createFileControl(
      "Please upload a high-resolution photo of yourself (500 px by 500 px or higher)",
      "",
      2,
      ["jpg", "jpeg", "png"]
    ),
    email: createTextControl("What email address can we reach you at?"),
    phone: createTextControl("What's the best phone number to reach you?"),
    intendedAudience: createRadioControl("Intended audience", audienceOptions),
    speakerBio: createTextareaControl("Speaker bio (250 words max)"),
    speakerTopicCategory: createRadioControl(
      "Which category does your speaker topic fit in?",
      speakerTopicCategoryOptions,
      "( optional )"
    ),
    otherSpeakerTopicCategory: createTextControl(
      "If you selected other, please specify"
    ),
    sessionTitle: createTextControl("Proposed session title (10 words max)"),
    links: createTextControl(
      "Please share any links to some recent speaking engagements you have been a part of. Links to video or audio are preferred."
    ),
    diversityForYou: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.techInvestor]: {
    // intro: createIntroControl(
    //   "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams",
    //   [
    //     "The Investor Program seeks to strengthen pathways to tech-enabled investments, increase access to dealflow, and expand pathways to underestimated founders.",
    //     "Eligible investors must be available to attend in person (Edmonton, AB) or virtually during the festival and must be actively investing in startups. Investors are generally investing in pre-seed, seed, and/or Series A rounds.",
    //   ]
    // ),
    intro: createIntroControl(
      "Register for the C-Tribe x Tech Investor's Program",
      [
        "The Investor Program seeks to strengthen pathways to tech-enabled investments, increase access to dealflow, and expand pathways to underestimated founders.",
        "Eligible investors must be available to attend in person or virtually during the events and must be actively investing in startups. Investors are generally investing in pre-seed, seed, and/or Series A rounds.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    fundName: createTextControl("What is your fund name?"),
    jobTitle: createTextControl("What is your job title?"),
    race: createCheckboxGroupControl(
      "How Would You Describe Your Race?",
      raceOptions
    ),
    gender: createCheckboxControl(
      "How would you describe your gender?",
      genderOptions
    ),
    isCompanyInCanada: createRadioControl(
      "Is your company incorporated in and legally operating in Canada?",
      defaultRadioOptions
    ),
    headquartersCity: createTextControl(
      "Which city is your headquarter location?"
    ),
    headquartersProvinceState: createTextControl(
      "Which province/state is your headquarter located in?"
    ),
    headquartersCountry: createTextControl(
      "Which country is your headquarters located in?"
    ),
    fundFocus: createRadioControl(
      "Does your fund focus on B2B or B2C?",
      fundFocusOptions
    ),
    investmentStages: createCheckboxControl(
      "Which stage do you invest in?",
      investmentStageOptions
    ),
    industry: createCheckboxControl(
      "What industries do you invest in?",
      industryOptions
    ),
    otherIndustry: createTextControl("If you selected other, please specify"),
    ventureFundDescription: createTextControl(
      "Describe your venture fund in 2-3 sentences"
    ),
    investingExperience: createRadioControl(
      "How long have you been investing in startups?",
      investingExperienceOptions
    ),
    fundWebsite: createTextControl(
      "If you have a website for your fund, please share the address"
    ),
    logo: createFileControl("Please upload your company's logo", "", 5, [
      "pdf",
    ]),
    diversityForYou: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.techEngineers]: {
    intro: createIntroControl(
      "Software Engineer / Developer? Apply for free C-Tribe Festival tickets",
      [
        "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams. The Software Engineer program seeks to reward the most active developers and open-source contributors in our network and strengthen pathways for more emerging engineers and developers.",
        "Apply below for your chance to receive two Tribe Passes to the entire C-Tribe Festival & Conference.",
      ]
    ),
    companyName: createTextControl(
      "What is your company name?",
      "( optional )"
    ),
    jobTitle: createTextControl("What is your job title?"),
    race: createCheckboxGroupControl(
      "How Would You Describe Your Race?",
      raceOptions,
      "Select all that apply"
    ),
    gender: createCheckboxControl(
      "How would you describe your gender?",
      genderOptions,
      "Select all that apply"
    ),
    city: createTextControl("Which city are you located in?"),
    province: createTextControl("Which province/state are you located in?"),
    country: createTextControl("Which country are you located in?"),
    mainIndustry: createRadioControl(
      "What is your main industry?",
      industryOptions
    ),
    additionalIndustries: createCheckboxControl(
      "Which additional industries do you have an interest in?",
      industryOptions,
      "Please select a maximum of three options."
    ),
    otherIndustry: createTextControl("If you selected Other, please specify"),
    websiteUrl: createTextControl(
      "If you have a website for your business, please share the address",
      "( optional )"
    ),
    githubUrl: createTextControl("Please share your GitHub profile URL"),
    diversityForYou: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.techExecLuncheonGeneralAdmission2k25]: techExecLuncheonFormControls,
  [formSlugs.techCBlackSemiFinals]: cBlackTechPitchSemiFinalsFormControls,
  [formSlugs.techCElevateHerSemiFinals]:
    cElevateHerTechPitchSemiFinalsFormControls,

  [formSlugs.gamingEsportOverwatch]: {
    intro: createIntroControl(
      "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams",
      [
        "Local and global teams are invited to face off against the best in their quest for their part of $1,000 prize pool and the title of C-Tribe x Gaming Esports World Open Champion. Register before July 15, 2027. Matches take place during C-Tribe Festival, August 12-17, 2027.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    discordId: createTextControl("What is your discord id?"),
    dob: createTextControl("What is your date of birth?"),
    overwatchRank: createTextControl("What is your Current Overwatch Rank?"),
    highestRank: createTextControl("What’s the highest you’ve ever received?"),
    overwatchRole: createTextControl("What role do you play proficiently?"),
    releasePlan: createTextControl("When do you plan to release it?"),
    city: createTextControl("Which city are you from?"),
    teamName: createTextControl(
      "Do you have a team? If so, each member will have to register separately. Please list the name of your team below if you have one."
    ),
    teamLogo: createFileControl(
      "Please upload your team logo if you have one (500 px by 500 px)",
      "",
      5,
      ["jpg", "jpeg", "png"]
    ),
    missionAndDiversity: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.gamingSpeaker]: {
    intro: createIntroControl("Apply as a speaker for C-Tribe x Gaming 2027", [
      "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams. We curate deep dives into specific predetermined topics, covering a comprehensive range of game development disciplines.",
      "We're looking for speakers who align with that goal of providing useful insight and advice into the use cases of The Future Trends, the Business of Gaming, Indie Game Development, and Creative Sector Guidance and many more.",
      "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams.",
      "We're looking for speakers who align with that goal of providing useful insight and advice into the use cases of self-improvement, emerging technology, business, sales & marketing, product, operations, creative sector guidance and many more.",
    ]),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    companyName: createTextControl("What is your company name?"),
    jobTitle: createTextControl("What is your job title?"),
    photo: createFileControl(
      "Please upload a high-resolution photo of yourself (500 px by 500 px or higher)",
      "Allow for jpg and png uploads of up to 2 MB",
      2,
      ["jpg", "png"]
    ),
    email: createTextControl("What email address can we reach you at?"),
    phone: createTextControl("What's the best phone number to reach you?"),
    intendedAudience: createRadioControl("Intended audience", audienceOptions),
    speakerBio: createTextareaControl("Speaker bio (250 words max)"),
    speakerTopicCategory: createRadioControl(
      "Which category does your speaker topic fit in?",
      speakerTopicCategoryOptions
    ),
    otherSpeakerTopicCategory: createTextControl(
      "If you selected other, please specify"
    ),
    sessionTitle: createTextControl("Proposed session title (10 words max)"),
    links: createTextControl(
      "Please share any links to some recent speaking engagements you have been a part of. Links to video or audio are preferred."
    ),
    diversityForYou: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.gamingDeveloper]: {
    intro: createIntroControl(
      "Game Developer or aspiring studio owner? Apply for free C-Tribe x Gaming passes",
      [
        "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams. The C-Tribe x Gaming Developer program seeks to reward the most active game developers in our network and strengthen pathways for more emerging developers and designers.  Apply below for your chance to receive two Gaming Passes.",
      ]
    ),
    companyName: createTextControl(
      "What is your company name?",
      "( optional )"
    ),
    jobTitle: createTextControl(
      "Job title or please share that you’re a student if you’re still in school."
    ),
    race: createCheckboxGroupControl(
      "How Would You Describe Your Race?",
      raceOptions,
      "Select all that apply"
    ),
    gender: createCheckboxControl(
      "How would you describe your gender?",
      genderOptions,
      "Select all that apply"
    ),
    city: createTextControl("Which city are you located in?"),
    province: createTextControl("Which province/state are you located in?"),
    country: createTextControl("Which country are you located in?"),
    websiteUrl: createTextControl(
      "if you have a personal website, portfolio or website for your company, please share the URL here",
      "( optional )"
    ),
    diversityForYou: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.gamingPitchQuest]: {
    intro: createIntroControl(
      "Register for the C-Tribe x Gaming Pitch Quest 2027",
      [
        "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people their dreams. ",
        "We're looking for emerging and established game developers that want to pitch their new game for a chance to win some prize money and connect with industry peers and experts!",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    jobTitle: createTextControl("What is your job title?"),
    studioName: createTextControl(
      "What is your studio name?",
      "It doesn't have to be a legal entity at this stage"
    ),
    gameName: createTextControl(
      "What is the name of your game?",
      "It doesn't have to be a legal entity at this stage"
    ),
    expectedReleaseDate: createTextControl(
      "When do you plan to release it?",
      "It doesn't have to be a legal entity at this stage"
    ),
    gameDescription: createTextareaControl(
      "Please share a description of your game (250 words max)"
    ),
    teamSize: createRadioControl(
      "How big is your team?",
      gamingPitchQuestTeamOptions
    ),
    gamePlatform: createCheckboxControl(
      "Which platform is your game being developed?",
      gamingPitchQuestPlatformOptions,
      "Select all that apply"
    ),
    otherGamePlatform: createTextControl(
      "If you selected other, please specify"
    ),
    businessModel: createTextControl(
      "What's your game's business model (aka., how will you make money)?"
    ),
    gameGenre: createTextControl("Describe the genre of your game"),
    website: createTextControl(
      "If you have a website for your game, please share it below"
    ),
    trailerUrl: createTextControl(
      "Please share a link to your game trailer",
      "( optional )"
    ),
    photo: createFileControl(
      "Please upload a high resolution photo of yourself (500 px by 500 px or higher)",
      "",
      5,
      ["jpg", "jpeg", "png"]
    ),
    companyLogo: createFileControl(
      "Please upload a high-resolution image of your company's logo (500 px by 500 px or higher)",
      "( optional )",
      5,
      ["jpg", "jpeg", "png"]
    ),
    email: createTextControl(
      "What is your email address ?",
      "This is only to get in touch, not to send spam"
    ),
    phoneNumber: createTextControl(
      "What's the best phone number to reach you?",
      "We will notify you via text about important details for your speaking engagement and additional C-Tribe Festival information. You can opt out at any time."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.gamingPlayTestZone]: {
    intro: createIntroControl(
      "C-Tribe x Gaming PlayTest Zone Registration Form 2027",
      [
        "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams.",
        "We're looking for exhibitors looking to build contacts, expose the latest developments in their game, get real-time customer feedback from gaming enthusiasts, and connect with the gaming industry.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    companyName: createTextControl("What is your studio or company name?"),
    jobTitle: createTextControl("What is your job title?"),
    photo: createFileControl(
      "Please upload a high resolution photo of yourself (500 px by 500 px or higher)",
      "",
      5,
      ["png"]
    ),
    email: createTextControl("What email address can we reach you at?"),
    phoneNumber: createTextControl(
      "What's the best phone number to reach you?"
    ),
    videoLink: createTextControl(
      "Please share a link to your game trailer or your company’s highlight video."
    ),
    description: createTextareaControl(
      "Game or company description you will be exhibiting (250 words max)"
    ),
    diversityForYou: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.vendor]: {
    businessName: createTextControl("What is your company/ business name?"),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    phoneNumber: createTextControl("What is your phone number?"),
    email: createTextControl("What is your email address?"),
    streetAddress: createTextControl("What is your street address?"),
    city: createTextControl("What city are you located in?"),
    state: createTextControl("How about your province or state?"),
    postalCode: createTextControl("What is your postal code or /zip?"),
    country: createTextControl("What country are you located in?"),
    urlsToShare: createTextareaControl(
      "Do you have website or social media to share?",
      "Please list all URLs that are applicable"
    ),
    itemsDescription: createTextareaControl(
      "Please describe the primary items that you want to showcase and/or sell at the Village Expo."
    ),
    gender: createCheckboxControl(
      "Please select all that apply to yourself",
      genderOptions,
      "Select all that apply"
    ),
    race: createCheckboxGroupControl(
      "What is the founder's ethnicity?",
      raceOptions,
      "Select all that apply"
    ),
    age: createRadioControl("Select the founder’s age range.", ageOptions),
    mainIndustry: createRadioControl(
      "What is the main industry of your business?",
      industryOptions
    ),
    isContributeToEnvironment: createRadioControl(
      "Does your company contribute to environmental restoration or preservation?",
      contributeToEnvironmentOptions
    ),
    companySize: createRadioControl(
      "What is the size of your company",
      companySizeOptions
    ),
    companyInBusiness: createRadioControl(
      "How many years has your company been in business?",
      companyInBusinessOptions
    ),
    companyCategory: createRadioControl(
      "Is your company inclusive, diverse-owned, and product-focused on aiding a specific minority?",
      companyCategoryOptions
    ),
    isNewVendor: createRadioControl(
      "Are you a first year or returning vendor at C-Tribe?",
      newVendorOptions
    ),
    vendorAcknowledgement: createCheckboxControl(
      "I acknowledge the prohibited items/services to vend at C-Tribe by checking these boxes :",
      vendorAcknowledgementOptions,
      "Select all that apply"
    ),
    // Retain historical labels for admin review; the applicant renderer selects
    // only the current Village questions for the chosen segment.
    ...villageVendorControls,
  },

  [formSlugs.villageInterest]: villageInterestControls,
  [formSlugs.filmSubmission]: filmSubmissionControls,
  [formSlugs.filmPitch]: filmPitchControls,
  [formSlugs.filmIndustry]: filmIndustryControls,
  [formSlugs.techStartupAlpha]: startupAlphaControls,

  [formSlugs.fashionDesigner]: {
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    email: createTextControl("What is your email address?"),
    streetAddress: createTextControl("What is your street address?"),
    city: createTextControl("What city are you located in?"),
    province: createTextControl("Which province/state are you located in?"),
    country: createTextControl("What country are you located in?"),
    postalCode: createTextControl("What is your postal code?"),
    numberOfLooks: createRadioControl(
      "How many total looks are in your collection?",
      numberOfLooksOptions
    ),
    websiteUrl: createTextControl(
      "If you have a website for your work, please share the address",
      "( optional )"
    ),
    brandName: createTextControl("What is your brand name?"),
    collectionName: createTextControl("What is the name of your collection?"),
    packageSelected: {
      ...createRadioControl("Which package are you applying for?", fashionPackageOptions),
      notes: [...fashionPackageNotes.august],
      notesLayout: "spaced",
    },
    selectionCategory: createCheckboxControl(
      "Please select the selection category",
      fashionSelectionCategoryOptions,
      "Select all that apply"
    ),
    isInTouchWithCTribeTeam: createRadioControl(
      "Have you been in touch with the C-Tribe team before?",
      defaultRadioOptions
    ),
    inTouchWith: createTextControl(
      "Who have you been in touch with?",
      "Their name, or how you connected (for example, at an event or by email)"
    ),
    instagramHandle: { ...createTextControl("What is your brand's Instagram handle?"), example: "@yourbrand" },
    additionalInfo: createTextareaControl(
      "Any other additional info you want to share?"
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.fashionBuyer]: {
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    email: createTextControl("What is your email address?"),
    streetAddress: createTextControl("What is your street address?"),
    city: createTextControl("What city are you located in?"),
    province: createTextControl("Which province/state are you located in?"),
    country: createTextControl("What country are you located in?"),
    postalCode: createTextControl("What is your postal code?"),
    phoneNumber: createTextControl("What is your phone number?"),
    companyName: createTextControl("What is your company / agency name?"),
    websiteUrl: createTextControl("What is your website?", "( optional )"),
    positionInCompany: createTextControl(
      "What is your position in Company / Agency?"
    ),
    businessType: createRadioControl(
      "What is your type of business?",
      businessTypeOptions
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.fashionStylist]: {
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    email: createTextControl("What is your email address?"),
    phoneNumber: createTextControl("What is your phone number?"),
    socialMedialhandle: {
      ...createTextControl("What is your social media handle?", "( optional )"),
      example: "@yourhandle",
    },
    availability14thNov: createCheckboxControl(
      "What is your availability, if any, on August 14th:",
      availabilityOptions,
      "Please indicate your availability for each day. Please note that upon scheduling, priority will be given to those with the most open and flexible availability"
    ),
    availability15thNov: createCheckboxControl(
      "What is your availability, if any, on August 15th:",
      availabilityOptions,
      "Please indicate your availability for each day. Please note that upon scheduling, priority will be given to those with the most open and flexible availability"
    ),
    portfolio: createFileControl("Please submit your portfolio", "", 5, [
      "pdf",
      "doc",
    ]),
    resume: createFileControl("Please submit your resume", "", 5, [
      "pdf",
      "doc",
    ]),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.fashionMua]: {
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    email: createTextControl("What is your email address?"),
    phoneNumber: createTextControl("What is your phone number?"),
    socialMedialhandle: {
      ...createTextControl("What is your social media handle?", "( optional )"),
      example: "@yourhandle",
    },
    availability14thNov: createCheckboxControl(
      "What is your availability, if any, on August 14th:",
      availabilityOptions,
      "Please indicate your availability for each day. Please note that upon scheduling, priority will be given to those with the most open and flexible availability"
    ),
    availability15thNov: createCheckboxControl(
      "What is your availability, if any, on August 15th:",
      availabilityOptions,
      "Please indicate your availability for each day. Please note that upon scheduling, priority will be given to those with the most open and flexible availability"
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.fashionModel]: {
    intro: createIntroControl("C-Tribe x Fashion 2027 Model Application", [
      "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people their dreams. ",
      "We're looking for models to be part of our next fashion shows in August 2027.",
      "Models must be 18 or older.",
    ]),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    availableDays: createCheckboxControl(
      "Please select which days you are available.",
      fashionModelAvailableDays,
      "Call times begin at 4:30 PM."
    ),
    email: createTextControl("What is your email address?"),
    phoneNumber: createTextControl("What is your phone number?"),
    socialMedialhandle: {
      ...createTextControl("What is your social media handle?", "Visit ours at @ctribexfashion!"),
      example: "@yourhandle",
    },
    isRepresentedByAgency: createRadioControl(
      "Are you represented by an agency?",
      defaultRadioOptions
    ),
    agencyName: createTextControl(
      "If you selected yes, what’s the name of your agency?"
    ),
    isPriorModelingExperience: createRadioControl(
      "Do you have prior modeling experience?",
      defaultRadioOptions
    ),
    isAgeAbove18: createRadioControl(
      "Are you 18 or older?",
      defaultRadioOptions,
      "Models must be 18 or older on the day of the show. We can't accept applications from anyone under 18."
    ),
    height: {
      ...createTextControl("What is your height?", "Feet and inches, or centimetres."),
      example: "5'9\" or 175 cm",
    },
    bustMeasurement: {
      ...createTextControl("What is your bust or chest measurement?", "In inches, measured around the fullest part. Use your comp card if you have one. You can also write cm."),
      example: "34",
    },
    waistMeasurement: {
      ...createTextControl("What is your waist measurement?", "In inches, measured around the narrowest part. You can also write cm."),
      example: "26",
    },
    hipsMeasurement: {
      ...createTextControl("What is your hip measurement?", "In inches, measured around the fullest part. You can also write cm."),
      example: "36",
    },
    inseam: {
      ...createTextControl("What is your inseam?", "( optional ) In inches, from the crotch to the ankle. Helps us fit trousers."),
      example: "32",
    },
    clothingSize: {
      ...createTextControl("What clothing size do you usually wear?", "Your usual size in Canadian or US sizing, for example a dress size, a jacket size, or S, M or L."),
      example: "Dress 4, or Jacket 40R, or M",
    },
    shoeSizeType: createRadioControl("Is your shoe size in women's or men's sizing?", modelShoeSizeTypeOptions),
    shoeSize: {
      ...createTextControl("What is your shoe size?", "US or Canadian size. Half sizes are fine."),
      example: "8.5",
    },
    hairColor: {
      ...createTextControl("What is your hair colour?", "As it is now. If you're planning to change it before the show, tell us in this answer."),
      example: "Dark brown",
    },
    eyeColor: {
      ...createTextControl("What is your eye colour?"),
      example: "Hazel",
    },
    videoUrl: createTextControl(
      "For out of town applications: If you'd like to submit a video casting, please upload to YouTube or Vimeo and share the link",
      "Please do not password protect the video"
    ),
    acceptTerms: createAcceptControl(fashionModelAcceptControlTerms),
  },

  [formSlugs.fashionProfessionals]: {
    intro: createIntroControl(
      "Apply as a Fashion Professional for C-Tribe x Fashion 2027.",
      [
        "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams.",
        "We're looking for industry professionals to join us for this special event as we share insights, expand networks, and discover new opportunities in the sector.",
        "Representing a brand? This application covers you only. Each brand you represent needs to apply separately, through the C-Tribe x Fashion designer application or the Fashion Marketplace application.",
        "If you're approved, you'll still need to buy a pass to attend.",
      ]
    ),
    companyName: createTextControl(
      "What is your company name?",
      "( optional )"
    ),
    jobTitle: createTextControl("What is your job title?"),
    race: createCheckboxGroupControl(
      "How Would You Describe Your Race?",
      raceOptions,
      "Select all that apply"
    ),
    gender: createCheckboxControl(
      "How would you describe your gender?",
      genderOptions,
      "Select all that apply"
    ),
    city: createTextControl("Which city are you located in?"),
    province: createTextControl("Which province/state are you located in?"),
    country: createTextControl("Which country are you located in?"),
    mainIndustry: createRadioControl(
      "What is your main industry?",
      industryOptions
    ),
    additionalIndustries: createCheckboxControl(
      "Which additional industries do you have an interest in?",
      industryOptions,
      "Please select a maximum of three options."
    ),
    otherIndustry: createTextControl("If you selected Other, please specify"),
    websiteUrl: createTextControl(
      "If you have a website for your business, please share the address",
      "( optional )"
    ),
    attendanceGoals: createTextareaControl(
      "What are your goals for attending C-Tribe x Fashion?",
      "For example: meeting designers, sourcing brands, finding talent or building partnerships."
    ),
    diversityForYou: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underestimated communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.fashionExhibit]: {
    intro: createIntroControl(
      "C-Tribe x Fashion Marketplace: Booth and Activation Application",
      [
        "Sell, showcase, or launch a drop at C-Tribe x Fashion: our standalone runway show on Thursday, February 25, 2027 in Edmonton, and C-Tribe x Fashion events at the August 12-17, 2027 festival.",
        "There is no application fee. Packages are indicative CAD prices. No payment is taken when you apply; accepted vendors receive payment details from a ctribefestival.com email address only.",
        "Drops run first-come with per-person limits or timed slots. Raffles, mystery boxes, and chance-based releases are not permitted. Resale items must be authentic.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    email: createEmailControl("What is your email address?"),
    phoneNumber: createPhoneControl("What is your phone number?"),
    companyName: createTextControl("What is your brand or company name?"),
    websiteUrl: createUrlControl(
      "What is your website or portfolio URL?",
      "e.g. https://www.yourbrand.com, include https://"
    ),
    instagramHandle: { ...createTextControl("What is your Instagram handle?"), example: "@yourbrand" },
    exhibitEvents: createCheckboxControl(
      "Which C-Tribe x Fashion events are you applying for?",
      fashionExhibitEventOptions,
      "Select all that apply. February vendors get priority booking for August."
    ),
    exhibitType: createCheckboxControl(
      "What type of exhibit are you applying for?",
      fashionExhibitTypeOptions,
      "Select all that apply"
    ),
    productCategories: createCheckboxControl(
      "What do you sell or showcase?",
      fashionExhibitCategoryOptions,
      "Select all that apply"
    ),
    packagePreference: createRadioControl(
      "Which package interests you most?",
      fashionExhibitPackageOptions,
      "Indicative CAD prices. Final inclusions, pricing, and placement are confirmed on acceptance."
    ),
    priceRange: createRadioControl(
      "What is the typical price range of your products?",
      fashionExhibitPriceRangeOptions
    ),
    resaleStatus: createRadioControl(
      "Do you sell resale items?",
      fashionExhibitResaleOptions
    ),
    authenticityMethod: createTextareaControl(
      "If you sell resale items, how do you verify authenticity?",
      "( optional ) For example, third-party authentication or receipts from the original retailer.",
      true
    ),
    dropInterest: createRadioControl(
      "Would you like to release a limited drop at the event?",
      fashionExhibitDropOptions
    ),
    exhibitDescription: createTextareaControl(
      "Describe what you will be showcasing",
      "Tell us about your brand, products, or concept and what attendees can expect at your booth."
    ),
    isInTouchWithCTribeTeam: createRadioControl(
      "Have you been in touch with the C-Tribe team before?",
      defaultRadioOptions
    ),
    inTouchWith: createTextControl(
      "Who have you been in touch with?",
      "Their name, or how you connected (for example, at an event or by email)"
    ),
    additionalInfo: createTextareaControl(
      "Any other additional info you want to share?",
      "( optional )"
    ),
    acceptTerms: createAcceptControl(
      'By hitting \'I accept\', I confirm the information above is accurate and that I have authority to enter into the <a href="https://www.ctribefestival.com/terms" class="text-blue-500" target="_blank" rel="noopener noreferrer">Terms of Service</a> and the <a href="https://www.ctribefestival.com/photo-video-release" class="text-blue-500" target="_blank" rel="noopener noreferrer">Photo &amp; Video Release</a>, and have read the <a href="https://www.ctribefestival.com/privacy" class="text-blue-500" target="_blank" rel="noopener noreferrer">Privacy Policy</a>. C-Tribe will email me about this application.'
    ),
  },

  [formSlugs.fashionPartner]: {
    intro: createIntroControl(
      "C-Tribe x Fashion — Partnership Application",
      [
        "Partner with C-Tribe x Fashion and connect your brand with a vibrant community of designers, creatives, and culture-makers.",
        "Whether you're looking for brand visibility, community engagement, or talent access, we'll work with you to create a partnership that delivers real value.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    email: createEmailControl("What is your email address?"),
    phoneNumber: createPhoneControl("What is your phone number?"),
    companyName: createTextControl("What is your company or organization name?"),
    positionInCompany: createTextControl("What is your role or title?"),
    websiteUrl: createUrlControl(
      "What is your company website?",
      "( optional )"
    ),
    partnerTier: createRadioControl(
      "Which partnership tier are you interested in?",
      fashionPartnerTierOptions
    ),
    partnerGoals: createCheckboxControl(
      "What are your partnership goals?",
      fashionPartnerGoalOptions,
      "Select all that apply"
    ),
    partnerDescription: createTextareaControl(
      "Tell us about your organization and why you'd like to partner with C-Tribe x Fashion",
      "Include any specific ideas or activations you have in mind."
    ),
    isInTouchWithCTribeTeam: createRadioControl(
      "Have you been in touch with the C-Tribe team before?",
      defaultRadioOptions
    ),
    inTouchWith: createTextControl(
      "Who have you been in touch with?",
      "Their name, or how you connected (for example, at an event or by email)"
    ),
    additionalInfo: createTextareaControl(
      "Any other additional info you want to share?",
      "( optional )"
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.resumeUpload]: {
    intro: createIntroControl("Career information for C-Tribe review", [
      "C-Tribe reviews career information manually. This is not an automated employer-matching service or an event ticket.",
      "Career introductions are not open. Record your preference for C-Tribe review only. Separate approval for each named employer is required before sharing; no interview or employment is guaranteed.",
    ]),
    resumeSummary: createTextareaControl(
      "Resume Summary",
      "This is the summary or profile section on your resume."
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    professionalTitle: createTextControl("What is your professional title?"),
    email: createTextControl("What is your email?"),
    phoneNumber: createTextControl("What is your phone number?"),
    cityAndProvince: createTextControl(
      "What city and province are you located in?"
    ),
    skills: createTextareaControl("Please list your skills"),
    websiteUrl: createTextControl(
      "Please provide the URL for your company’s website or your personal portfolio",
      "( optional )"
    ),
    linkedInUrl: createTextControl(
      "What is your LinkedIn URL?",
      "( optional )"
    ),
    educationalHistory: createTextareaControl(
      "Please list your educational history"
    ),
    workExperience: createTextareaControl("Please list your work experience"),
    resume: createFileControl("Please upload your resume", "( optional )", 5, [
      "pdf",
      "doc",
    ]),
    resumeReviewConsent: createAcceptControl(resumeReviewConsent),
  },

  [formSlugs.volunteer]: {
    interests: createCheckboxWithNoteControl<keyof typeof AreaOfInterest>(
      "What are your areas of interest?",
      areaOfInterestOptionsWithNote,
      "Please choose what C-Tribe verticals you’re most interested in getting involved in."
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    email: createTextControl("What is your email address?"),
    phoneNumber: createTextControl("What is your phone number?"),
  },

  [formSlugs.mediaPress]: {
    intro: createIntroControl(
      "C-Tribe Media & Press Registration Form",
      [
        "Use this form if you're requesting media, press, or editorial access to C-Tribe Festival & Conference.",
        "We're looking for journalists, editors, photographers, broadcasters, and media teams who want to cover the festival and its community.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    email: createTextControl("What is your email address?"),
    phoneNumber: createTextControl("What is your phone number?"),
    organizationName: createTextControl(
      "What is the name of your media outlet or organization?"
    ),
    role: createTextControl("What is your role or title?"),
    coverageFocus: createTextareaControl(
      "What kind of coverage are you planning?",
      "(e.g., interviews, photography, editorial, recap, broadcast, or social coverage)"
    ),
  },

  [formSlugs.b2b]: {
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    jobPosition: createTextControl("What is your job position?"),
    phoneNumber: createTextControl("What is your phone number?"),
    profilePhoto: createFileControl(
      "Please upload your profile picture",
      "",
      5,
      ["png"]
    ),
    organizationName: createTextControl("What is your organization name?"),
    organizationType: createRadioControl(
      "What is your organization type?",
      organizationTypeOptions
    ),
    companyWebsite: createTextControl("What is your company website?"),
    organizationDescription: createTextareaControl(
      "What is your organization description?"
    ),
    organizationLogo: createFileControl(
      "Please upload your organization logo",
      "",
      5,
      ["pdf"]
    ),
    activityAreas: createCheckboxGroupControl(
      "What is your areas of activity?",
      activityAreasOptions
    ),
    intrestedServices: createCheckboxGroupControl(
      "What type of product or service are in primarily interested in finding a partner, product or service provider for",
      activityAreasOptions
    ),
    primaryGoal: createCheckboxControl(
      "What is the primary goal you aim to achieve their this relationship?",
      b2bPrimaryGoalOptions
    ),
    otherGoals: createTextControl("If you selected other, please specify"),
    address: createTextControl("What is your address?"),
    city: createTextControl("What city are you located in?"),
    province: createTextControl("What province/state are you located in?"),
    postalCode: createTextControl("What is your postal code?"),
    selectedSessions: createCheckboxControl(
      "Which sessions do you want to attend?",
      b2bSessionOptions,
      "Select all that apply"
    ),
    diversityForYou: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.influencer]: {
    intro: createIntroControl(
      "C-Tribe x Creator and Influencer Registration Form 2027",
      [
        "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams.",
        "We're looking for content creators and influencers to take part in the festival & conference. Each successful applicant will be provided with an all-access badge to explore the full festival offerings.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    urls: createTextControl(
      "Please share the URLs to your social media handles"
    ),
    primaryContentFocus: createTextControl(
      "What is your primary content focus?",
      "(e.g., fashion, travel, food, technology, etc.)"
    ),
    email: createTextControl("What email address can we reach you at?"),
    phoneNumber: createTextControl(
      "What's the best phone number to reach you?"
    ),
    aim: createTextareaControl(
      "What do you aim to achieve by participating as an influencer or content creator at this event?"
    ),
    diversityForYou: createTextareaControl(
      "C-Tribe's mission is to celebrate diversity and help underserved innovative and creative people achieve their dreams. How do you relate to this and what does diversity mean to you?",
      "C-Tribe aims to reduce the barriers of entry for underrepresented communities and would like to participate in the festival. Our current definition of diversity includes people of all genders, gender identity and expression, sexual orientations, disability, physical appearance, body size, race, and religion. Having these voices attend C-Tribe allows us to grow through interaction, hearing each other’s experiences, and sharing viewpoints."
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.generalSpeaker]: generalSpeakerFormControls,

  [formSlugs.musicArtist]: musicArtistFormControls,
  [formSlugs.musicPublisher]: musicPublisherFormControls,
  [formSlugs.musicPublisherPublisher]: musicPublisherPublisherFromControls,

  [formSlugs.diversityPass]: diversityPassFormControls,

  [formSlugs.photographer]: photographerFormControls,

  [formSlugs.vipDinner]: vipDinnerFormControls,

  [formSlugs.innovationZone]: innovationZoneFormControls,

  "hotel-booking": hotelBookingsFormControls,

  "marketing-site-popup": marketingSitePopUpsFormControls,

  [formSlugs.partnership]: {
    intro: createIntroControl(
      "C-Tribe Festival 2027 - Partnership Inquiry",
      [
        "C-Tribe Festival is Canada's premier diversity-led collision gathering, August 12-17, 2027 in Edmonton, AB.",
        "We bring together 25,000+ attendees across Tech, Fashion, Gaming, Music, Culture, Film, and Health. Tell us about your organization and how you'd like to be involved.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    companyName: createTextControl("What is your company or organization name?"),
    jobTitle: createTextControl("What is your job title?"),
    email: createEmailControl("What email address can we reach you at?"),
    phone: createPhoneControl("What's the best phone number to reach you?"),
    companyWebsite: createUrlControl(
      "What is your company website?",
      "( optional )",
      true
    ),
    companySize: createRadioControl(
      "What is the size of your organization?",
      companySizeOptions
    ),
    mainIndustry: createRadioControl(
      "What industry is your organization in?",
      industryOptions
    ),
    areaOfInterest: createCheckboxWithNoteControl(
      "Which vertical(s) are you interested in partnering with?",
      areaOfInterestOptionsWithNote,
      "Select all that apply"
    ),
    partnershipTier: createRadioControl(
      "What level of partnership are you considering?",
      partnershipTierOptions
    ),
    partnershipGoals: createTextareaControl(
      "What are your partnership goals? What does your organization hope to achieve through this partnership?",
      "",
      false,
      1,
      300,
      TextLengthCheckOptionType.WORD_COUNT
    ),
    additionalInfo: createTextareaControl(
      "Anything else you'd like us to know?",
      "( optional )",
      true
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.film]: {
    intro: createIntroControl(
      "C-Tribe x Film 2027 - Filmmaker Submission",
      [
        "C-Tribe x Film is a platform where the intersection of art and cinema gives rise to captivating narratives and unique perspectives.",
        "We're looking for filmmakers to submit their work for screening and showcase opportunities during C-Tribe Festival 2027.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    email: createEmailControl("What email address can we reach you at?"),
    phone: createPhoneControl("What's the best phone number to reach you?"),
    filmTitle: createTextControl("What is the title of your film?"),
    filmGenre: createRadioControl(
      "What genre best describes your film?",
      filmGenreOptions
    ),
    filmDuration: createTextControl(
      "What is the runtime of your film?",
      "e.g., 12 minutes, 90 minutes"
    ),
    filmSynopsis: createTextareaControl(
      "Film synopsis (250 words max)",
      "",
      false,
      1,
      250,
      TextLengthCheckOptionType.WORD_COUNT
    ),
    filmLink: createUrlControl(
      "Screener link (Vimeo, YouTube, or other streaming link)",
      "Password-protected links are fine. Include the password in the additional info field below."
    ),
    previousScreenings: createTextareaControl(
      "Has this film screened at other festivals or events? If so, where?",
      "( optional )",
      true
    ),
    socialMediaHandle: createUrlControl(
      "Your social media or website link",
      "( optional )",
      true
    ),
    additionalInfo: createTextareaControl(
      "Anything else you'd like us to know? (e.g., screener password, special requirements)",
      "( optional )",
      true
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.artist]: {
    intro: createIntroControl(
      "C-Tribe x Culture 2027 - Artist Application",
      [
        "C-Tribe x Culture is an immersive gathering that includes cultural activations, art installations, workshops, and one-of-a-kind experiences.",
        "We're looking for visual artists, performers, and creators to bring their work to C-Tribe Festival 2027.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    artistName: createTextControl(
      "What is your artist or stage name?",
      "( optional )",
      true
    ),
    email: createEmailControl("What email address can we reach you at?"),
    phone: createPhoneControl("What's the best phone number to reach you?"),
    artForm: createRadioControl(
      "What best describes your art form?",
      artFormOptions
    ),
    artistStatement: createTextareaControl(
      "Tell us about your work and what you'd like to present at C-Tribe (250 words max)",
      "",
      false,
      1,
      250,
      TextLengthCheckOptionType.WORD_COUNT
    ),
    portfolioLink: createUrlControl(
      "Link to your portfolio, website, or social media"
    ),
    additionalInfo: createTextareaControl(
      "Anything else you'd like us to know? (e.g., space requirements, technical needs)",
      "( optional )",
      true
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.health]: {
    intro: createIntroControl(
      "C-Tribe x Health 2027 - Practitioner Application",
      [
        "C-Tribe x Health brings together wellness practitioners, health professionals, and community health advocates.",
        "We're looking for practitioners to lead workshops, demos, and wellness experiences during C-Tribe Festival 2027.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    email: createEmailControl("What email address can we reach you at?"),
    phone: createPhoneControl("What's the best phone number to reach you?"),
    practiceArea: createRadioControl(
      "What area of health and wellness do you specialize in?",
      healthPracticeAreaOptions
    ),
    credentials: createTextControl(
      "What are your relevant credentials or certifications?",
      "e.g., RD, CPT, RMT, PhD"
    ),
    bio: createTextareaControl(
      "Tell us about your practice and what you'd like to offer at C-Tribe (250 words max)",
      "",
      false,
      1,
      250,
      TextLengthCheckOptionType.WORD_COUNT
    ),
    websiteUrl: createUrlControl(
      "Link to your website or social media",
      "( optional )",
      true
    ),
    additionalInfo: createTextareaControl(
      "Anything else you'd like us to know? (e.g., equipment needs, space requirements)",
      "( optional )",
      true
    ),
    acceptTerms: createAcceptControl(),
  },

  [formSlugs.videographer]: {
    intro: createIntroControl(
      "C-Tribe Festival 2027 Videographer & Photographer Application",
      [
        "C-Tribe Festival is looking for videographers and photographers to help capture the energy of the festival across all verticals: tech, fashion, music, gaming, culture, film and health.",
        "Whether you shoot long-form podcast content, short-form social reels, event highlights, or editorial stills, we want to hear from you. Selected creatives receive an all-access media badge, and paid work is also available.",
      ]
    ),
    firstName: createTextControl("What is your first name?"),
    lastName: createTextControl("What is your last name?"),
    email: createEmailControl("What email address can we reach you at?"),
    phone: createPhoneControl("What's the best phone number to reach you?"),
    primaryMedium: createRadioControl(
      "What is your primary medium?",
      videographerMediumOptions
    ),
    contentStyle: createCheckboxControl(
      "What content styles do you specialize in? (select all that apply)",
      videographerContentStyleOptions
    ),
    portfolioLink: createUrlControl(
      "Link to your portfolio, reel, or website"
    ),
    gearLongform: createTextareaControl(
      "What camera and audio gear do you typically use for long-form or podcast-style content?",
      "e.g., Sony A7IV, Rode VideoMic Pro, teleprompter setup...",
      true
    ),
    gearShortform: createTextareaControl(
      "What camera and gear do you typically use for short-form, reel-style, or event content?",
      "e.g., iPhone 15 Pro, DJI RS3, wireless lav mic...",
      true
    ),
    halfDayRate: createTextControl(
      "What is your half-day rate? (5 hours, raw footage only)",
      "Enter a dollar amount"
    ),
    previousEventWork: createTextareaControl(
      "Have you covered festivals, conferences, or large-scale events before? Tell us about it.",
      "( optional )",
      true
    ),
    socialMediaHandle: createUrlControl(
      "Your social media handle or link",
      "( optional )",
      true
    ),
    basedIn: createTextControl(
      "Where are you based?",
      "e.g., Edmonton, AB"
    ),
    canTravel: createRadioControl(
      "Are you available to travel to Edmonton, AB for August 12-17, 2027?",
      defaultRadioOptions
    ),
    travelNotes: createTextareaControl(
      "If you're based outside Edmonton, how would you handle travel costs?",
      "( optional ) e.g., included in my rate, or quoted separately",
      true
    ),
    additionalInfo: createTextareaControl(
      "Anything else you'd like us to know? (e.g., availability, special equipment, questions)",
      "( optional )",
      true
    ),
    // Owner rule 2026-10-26, same as the February photographer form: exclusive
    // rights for C-Tribe, portfolio use allowed. Full wording: /photo-video-release/.
    usageRights: createRadioControl(
      "C-Tribe Society reserves the exclusive right to use, edit and publish all photos and media you capture at the event, in any format. Do you agree to give C-Tribe this right?",
      photographerFebRightsOptions,
      "You may still show a selection in your own portfolio, website and social media to promote your work. You may not sell or license event media to anyone else."
    ),
    acceptTerms: createAcceptControl(
      'By hitting \'I accept\', I confirm the information above is accurate and agree that C-Tribe representatives may contact me by email or phone about this application and the event. I have authority to enter into the <a href="https://www.ctribefestival.com/terms/" class="text-blue-500" target="_blank" rel="noopener noreferrer">Terms of Service</a> and the <a href="https://www.ctribefestival.com/photo-video-release/" class="text-blue-500" target="_blank" rel="noopener noreferrer">Photo &amp; Video Release</a>, and have read the <a href="https://www.ctribefestival.com/privacy/" class="text-blue-500" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.'
    ),
  },
};

// tech form schemas
const xTechPitchElevateHerSchema = {
  companyName: zodString("Company Name"),
  jobTitle: zodString("Job Title"),
  race: zodRaceOptionsSchema,
  isIdentifyWomanAndHaveGraterControlingEquity: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  gender: z
    .array(z.enum(genderOptions))
    .min(1, { message: ERROR_MESSAGES.FieldRequired("Gender") }),
  isCompanyInCanada: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  headquartersCity: zodString("Headquarters City"),
  headquartersProvinceState: zodString("Headquarters Province/State"),
  headquartersCountry: zodString("Headquarters Country"),
  isForProfit: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  mainIndustry: z.enum(industryOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  additionalIndustries: z
    .array(z.enum(industryOptions))
    .max(3, ERROR_MESSAGES.MaxAllowed(3)),
  otherIndustry: z.string().optional(),
  businessVentureDescription: zodString("Business Venture Description"),
  teamUniqueness: zodString("Team Uniqueness"),
  problemBeingSolved: zodString("Problem Being Solved"),
  solutionDescription: zodString("Solution Description"),
  targetedMarketSize: zodString("Targeted Market Size"),
  ventureDuration: z.enum(ventureDurationOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  websiteURL: zodUrl("Website", true),
  videoURL: zodUrl("Video", true),
  pitchDeckUpload: zodFileValidator("Pitch Deck Upload", ["pdf"], 5),
  relatesToMissionStatement: zodString("Relates To Mission Statement"),
  acceptTerms: z
    .enum(defaultRadioOptions)
    .refine((val) => val == "Yes", { message: ERROR_MESSAGES.AcceptTerms() }),
};

const xTechPitchCBlackSchema = {
  companyName: zodString("Company Name"),
  jobTitle: zodString("Job Title"),
  race: zodRaceOptionsSchema,
  isIdentifyBlackAndHaveGraterControlingEquity: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  gender: z
    .array(z.enum(genderOptions))
    .min(1, { message: ERROR_MESSAGES.FieldRequired("Gender") }),
  isCompanyInCanada: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  headquartersCity: zodString("Headquarters City"),
  headquartersProvinceState: zodString("Headquarters Province/State"),
  headquartersCountry: zodString("Headquarters Country"),
  isForProfit: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  mainIndustry: z.enum(industryOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  additionalIndustries: z
    .array(z.enum(industryOptions))
    .max(3, ERROR_MESSAGES.MaxAllowed(3)),
  otherIndustry: z.string().optional(),
  businessVentureDescription: zodString("Business Venture Description"),
  teamUniqueness: zodString("Team Uniqueness"),
  problemBeingSolved: zodString("Problem Being Solved"),
  solutionDescription: zodString("Solution Description"),
  targetedMarketSize: zodString("Targeted Market Size"),
  ventureDuration: z.enum(ventureDurationOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  websiteURL: zodUrl("Website", true),
  videoURL: zodUrl("Video", true),
  pitchDeckUpload: zodFileValidator("Pitch Deck Upload", ["pdf", "doc"], 5),
  relatesToMissionStatement: zodString("Relates To Mission Statement"),
  acceptTerms: z
    .enum(defaultRadioOptions)
    .refine((val) => val == "Yes", { message: ERROR_MESSAGES.AcceptTerms() }),
};

const xTechElevateApplicationSchema = {
  companyName: zodString("Company Name"),
  jobTitle: zodString("Job Title"),
  isCompanyInCanada: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  headquartersCity: zodString("Headquarters City"),
  headquartersProvinceState: zodString("Headquarters Province/State"),
  headquartersCountry: zodString("Headquarters Country"),
  isForProfit: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  mainIndustry: z.enum(industryOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  additionalIndustries: z
    .array(z.enum(industryOptions))
    .max(3, ERROR_MESSAGES.MaxAllowed(3)),
  otherIndustry: z.string().optional(),
  businessVentureDescription: zodString("Business Venture Description"),
  teamUniqueness: zodString("Team Uniqueness"),
  problemBeingSolved: zodString("Problem Being Solved"),
  solutionDescription: zodString("Solution Description"),
  targetedMarketSize: zodString("Targeted Market Size"),
  ventureDuration: z.enum(ventureDurationOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  websiteURL: zodUrl("Website", true),
  pitchDeckUpload: zodFileValidator("Pitch Deck Upload", ["pdf"], 5),
  acceptTerms: z
    .enum(defaultRadioOptions)
    .refine((val) => val == "Yes", { message: ERROR_MESSAGES.AcceptTerms() }),
};

const xTechSpeakerSignUpSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  companyName: zodString("Company Name"),
  jobTitle: zodString("Job Title"),
  photo: zodFileValidator("Photo", ["jpg", "png", "jpeg"], 2),
  email: zodEmailValidator(""),
  phone: zodPhoneNumberValidator,
  intendedAudience: z.enum(audienceOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  speakerBio: zodString("Speaker Bio"),
  speakerTopicCategory: z.enum(speakerTopicCategoryOptions, {
    required_error: ERROR_MESSAGES.FieldRequired(),
  }),
  otherSpeakerTopicCategory: z.string().optional(),
  sessionTitle: zodString("Session Title").refine(
    (val) => val && val?.split(" ").length < 10,
    "Max word count 10"
  ),
  links: zodUrl("Links", true),
  diversityForYou: zodString("Diversity For You"),
  acceptTerms: z
    .enum(defaultRadioOptions)
    .refine((val) => val == "Yes", { message: ERROR_MESSAGES.AcceptTerms() }),
};

const xTechStartupSignupSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  fundName: zodString("Fund Name"),
  jobTitle: zodString("Job Title"),
  race: zodRaceOptionsSchema,
  gender: z
    .array(z.enum(genderOptions))
    .min(1, { message: ERROR_MESSAGES.FieldRequired("Gender") }),
  isCompanyInCanada: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  headquartersCity: zodString("Headquarters City"),
  headquartersProvinceState: zodString("Headquarters Province/State"),
  headquartersCountry: zodString("Headquarters Country"),
  fundFocus: z.enum(fundFocusOptions, {
    required_error: ERROR_MESSAGES.FieldRequired(),
  }),
  investmentStages: z
    .array(z.enum(investmentStageOptions))
    .min(1, { message: ERROR_MESSAGES.FieldRequired() }),
  industry: z
    .array(z.enum(industryOptions))
    .min(1, { message: ERROR_MESSAGES.FieldRequired() })
    .max(10, { message: ERROR_MESSAGES.MaxAllowed(10) }),
  otherIndustry: z.string().optional(),
  ventureFundDescription: zodString("Venture Fund Description"),
  investingExperience: z.enum(investingExperienceOptions, {
    required_error: ERROR_MESSAGES.FieldRequired(),
  }),
  fundWebsite: zodUrl("Fund Website", true),
  logo: zodFileValidator("Logo", ["pdf"], 5),
  diversityForYou: zodString("Diversity For You"),
  acceptTerms: z
    .enum(defaultRadioOptions)
    .refine((val) => val == "Yes", { message: ERROR_MESSAGES.AcceptTerms() }),
};

const xTechEngineerSignUpSchema = {
  companyName: z.string().optional(),
  jobTitle: zodString("Job Title"),
  race: zodRaceOptionsSchema,
  gender: z
    .array(z.enum(genderOptions))
    .min(1, { message: ERROR_MESSAGES.FieldRequired("Gender") }),
  city: zodString("City"),
  province: zodString("Province/State"),
  country: zodString("Country"),
  mainIndustry: z.enum(industryOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  additionalIndustries: z
    .array(z.enum(industryOptions))
    .max(3, ERROR_MESSAGES.MaxAllowed(3)),
  otherIndustry: z.string().optional(),
  websiteUrl: zodUrl("Website", true),
  githubUrl: zodUrl("Github", false),
  diversityForYou: zodString("Field"),
  acceptTerms: z
    .enum(defaultRadioOptions)
    .refine((val) => val == "Yes", { message: ERROR_MESSAGES.AcceptTerms() }),
};

// gaming form schemas
const xGamingPitchQuestSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  discordId: zodString("Discord ID"),
  dob: z.string(),
  overwatchRank: zodString("Overwatch Rank"),
  highestRank: zodString("Highest Rank"),
  overwatchRole: zodString("Overwatch Role"),
  releasePlan: zodString("Release Plan"),
  city: zodString("City"),
  teamName: zodString("Team Name"),
  teamLogo: zodFileValidator("Team Logo", ["jpg", "png", "jpeg"], 5),
  missionAndDiversity: zodString("Mission and Diversity"),
  acceptTerms: z
    .enum(defaultRadioOptions)
    .refine((val) => val == "Yes", { message: ERROR_MESSAGES.AcceptTerms() }),
};

const xGamingSpeakerSignUpSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  companyName: zodString("Company Name"),
  jobTitle: zodString("Job Title"),
  photo: zodFileValidator("Photo", ["jpg", "png"], 2),
  email: zodEmailValidator(""),
  phone: zodPhoneNumberValidator,
  intendedAudience: z.enum(audienceOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  speakerBio: zodString("Speaker Bio"),
  speakerTopicCategory: z.enum(speakerTopicCategoryOptions, {
    required_error: ERROR_MESSAGES.FieldRequired(),
  }),
  otherSpeakerTopicCategory: z.string().optional(),
  sessionTitle: zodString("Session Title").refine(
    (val) => val && val?.split(" ").length < 10,
    "Max word count 10"
  ),
  links: zodUrl("Links", true),
  diversityForYou: zodString("Diversity For You"),
  acceptTerms: z
    .enum(defaultRadioOptions)
    .refine((val) => val == "Yes", { message: ERROR_MESSAGES.AcceptTerms() }),
};

const xGamingDeveloperSignUpSchema = {
  companyName: z.string().optional(),
  jobTitle: zodString("Job Title"),
  race: zodRaceOptionsSchema,
  gender: z
    .array(z.enum(genderOptions))
    .min(1, { message: ERROR_MESSAGES.FieldRequired("Gender") }),
  city: zodString("City"),
  province: zodString("Province/State"),
  country: zodString("Country"),
  websiteUrl: zodUrl("Website", true),
  diversityForYou: zodString("Field"),
  acceptTerms: zodAcceptTerms,
};

const gamingPitchQuestSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  jobTitle: zodString("Job Title"),
  studioName: zodString("Studio Name"),
  gameName: zodString("Game Name"),
  expectedReleaseDate: zodString("Expected Release Date"),
  gameDescription: zodString("Game Description").refine(
    (val) => val && val.length <= 250,
    { message: "Max 250 characters" }
  ),
  teamSize: z.enum(gamingPitchQuestTeamOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  gamePlatform: z
    .array(z.enum(gamingPitchQuestPlatformOptions))
    .min(1, ERROR_MESSAGES.SelectOne()),
  otherGamePlatform: z.string().optional(),
  businessModel: zodString("Business Model"),
  gameGenre: zodString("Game Genre"),
  website: zodUrl("Website", true),
  trailerUrl: zodUrl("Trailer URL", true),
  photo: zodFileValidator("Photo", ["jpg", "png", "jpeg"], 5),
  companyLogo: zodFileValidator(
    "Company Logo",
    ["jpg", "png", "jpeg"],
    5,
    true
  ),
  email: zodEmailValidator(""),
  phoneNumber: zodPhoneNumberValidator,
  acceptTerms: zodAcceptTerms,
};

const gamingPlayTestZoneSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  companyName: zodString("Studio/Company Name"),
  jobTitle: zodString("Job Title"),
  photo: zodFileValidator("Photo", ["png"], 5),
  email: zodEmailValidator(""),
  phoneNumber: zodPhoneNumberValidator,
  videoLink: zodUrl("Video Link", true),
  description: zodString("Field").refine(
    (val) => val && val.split(" ").filter((x) => !!x?.trim()).length <= 250,
    { message: "Max 250 words" }
  ),
  diversityForYou: zodString("Field"),
  acceptTerms: zodAcceptTerms,
};

// vendor form schema
const legacyVendorSchema = {
  businessName: zodString("Business Name"),
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  phoneNumber: zodPhoneNumberValidator,
  email: zodEmailValidator(""),
  streetAddress: zodString("Street Address"),
  city: zodString("City"),
  state: zodString("State"),
  postalCode: zodString("Postal Code"),
  country: zodString("Country"),
  urlsToShare: zodString("URLs to share"),
  itemsDescription: zodString("Primary Items Description"),
  gender: z
    .array(z.enum(genderOptions))
    .min(1, { message: ERROR_MESSAGES.FieldRequired("Gender") }),
  race: zodRaceOptionsSchema,
  age: z.enum(ageOptions, { required_error: ERROR_MESSAGES.SelectOne() }),
  mainIndustry: z.enum(industryOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  isContributeToEnvironment: z.enum(contributeToEnvironmentOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  companySize: z.enum(companySizeOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  companyInBusiness: z.enum(companyInBusinessOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  companyCategory: z.enum(companyCategoryOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  isNewVendor: z.enum(newVendorOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  vendorAcknowledgement: z
    .array(z.enum(vendorAcknowledgementOptions))
    .min(1, { message: ERROR_MESSAGES.FieldRequired("Field") }),
};

const vendorSchema = { ...legacyVendorSchema, ...villageVendorSchema };

// fashion form schema
const fashionDesignerSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  email: zodEmailValidator(""),
  streetAddress: zodString("Street Address"),
  city: zodString("City"),
  province: zodString("Province"),
  country: zodString("Country"),
  postalCode: zodString("Postal Code"),
  numberOfLooks: z.enum(numberOfLooksOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  websiteUrl: zodUrl("Website", true),
  brandName: zodString("Brand Name"),
  collectionName: zodString("Collection Name"),
  packageSelected: z.enum(fashionPackageOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  selectionCategory: z
    .array(z.enum(fashionSelectionCategoryOptions))
    .min(1, ERROR_MESSAGES.SelectOne()),
  isInTouchWithCTribeTeam: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  inTouchWith: zodString("Who you've been in touch with"),
  instagramHandle: zodString("Instagram Handle"),
  additionalInfo: zodString("Additional Info"),
  acceptTerms: zodAcceptTerms,
};

const fashionBuyerSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  email: zodEmailValidator(""),
  streetAddress: zodString("Street Address"),
  city: zodString("City"),
  province: zodString("Province"),
  country: zodString("Country"),
  postalCode: zodString("Postal Code"),
  phoneNumber: zodPhoneNumberValidator,
  companyName: zodString("Company Name"),
  websiteUrl: zodUrl("Website", true),
  positionInCompany: zodString("Position In Company"),
  businessType: z.enum(businessTypeOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  acceptTerms: zodAcceptTerms,
};

const fashionStylistSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  email: zodEmailValidator(""),
  phoneNumber: zodPhoneNumberValidator,
  socialMedialhandle: zodString("Social Media Handle", true),
  availability15thAug: z
    .array(z.enum(availabilityOptions))
    .min(1, ERROR_MESSAGES.SelectOne()),
  availability16thAug: z
    .array(z.enum(availabilityOptions))
    .min(1, ERROR_MESSAGES.SelectOne()),
  portfolio: zodFileValidator("Portfolio", ["pdf", "doc"], 5),
  resume: zodFileValidator("Portfolio", ["pdf", "doc"], 5),
  acceptTerms: zodAcceptTerms,
};

const fashionMuaSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  email: zodEmailValidator(""),
  phoneNumber: zodPhoneNumberValidator,
  socialMedialhandle: zodString("Social Media Handle", true),
  availability15thAug: z
    .array(z.enum(availabilityOptions))
    .min(1, ERROR_MESSAGES.SelectOne()),
  availability16thAug: z
    .array(z.enum(availabilityOptions))
    .min(1, ERROR_MESSAGES.SelectOne()),
  acceptTerms: zodAcceptTerms,
};

// Standalone February 25, 2027 runway show: same model questions as the
// festival call, but a single availability confirmation instead of the
// August day picker. Defined after formControls so it can reuse those entries.
const augustModelControls = formControls[formSlugs.fashionModel];
const sharedModelControls = Object.fromEntries(
  Object.entries(augustModelControls).filter(
    ([key]) => !["intro", "firstName", "lastName", "availableDays"].includes(key)
  )
);
formControls[formSlugs.fashionModelNovember2026] = {
  intro: createIntroControl(
    "C-Tribe x Fashion Show, February 25, 2027, Edmonton",
    [
      "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams.",
      "We're casting models for our standalone runway show on Thursday, February 25, 2027 in Edmonton. Applications close January 11, 2027 at 11:59 PM MST. This show is separate from the August 2027 festival; apply to both if you're interested in both.",
      "Models must be 18 or older.",
    ]
  ),
  firstName: augustModelControls.firstName,
  lastName: augustModelControls.lastName,
  isAvailableFebruary25: createRadioControl(
    "Are you available on Thursday, February 25, 2027 in Edmonton?",
    defaultRadioOptions,
    "Call time begins at 4:30 PM."
  ),
  ...sharedModelControls,
};

// Standalone February 25, 2027 runway show designer call. Same questions as
// the festival designer form, plus a February availability check.
const augustDesignerControls = formControls[formSlugs.fashionDesigner];
formControls[formSlugs.fashionDesignerFebruary2027] = {
  intro: createIntroControl(
    "C-Tribe x Fashion Show, February 25, 2027, Edmonton",
    [
      "The C-Tribe Festival aims to help celebrate diversity and help innovative and creative people achieve their dreams.",
      "We're selecting designers to show collections at our standalone runway show on Thursday, February 25, 2027 in Edmonton. Applications close December 14, 2026 at 11:59 PM MST. This show is separate from the August 2027 festival; apply to both if you're interested in both.",
    ]
  ),
  ...Object.fromEntries(
    Object.entries(augustDesignerControls)
      .filter(([key]) => key !== "acceptTerms")
      .map(([key, control]) => {
        if (key === "packageSelected") return [key, { ...control, notes: [...fashionPackageNotes.february] }];
        return [key, control];
      })
  ),
  isAvailableFebruary25: createRadioControl(
    "Are you available on Thursday, February 25, 2027 in Edmonton?",
    defaultRadioOptions
  ),
  acceptTerms: augustDesignerControls.acceptTerms,
};

// Photographers for the February 25, 2027 show. Professionals quoting paid
// coverage and creatives shooting for a badge both apply here, so every answer
// we need to compare them arrives in one place (owner: no calls, forms only).
// CTJAM, the C-Tribe Game Jam. Solo jammers and whole teams register here,
// so teams can be formed before the jam. Days and hours are set with the
// festival schedule; the intro says so rather than guessing.
formControls[formSlugs.gamingCtjam] = {
  intro: createIntroControl(
    "Register for CTJAM, the C-Tribe Game Jam",
    [
      "Make a game in under 72 hours at C-Tribe x Gaming, August 12-17, 2027 in Edmonton. We'll confirm the exact jam days and hours with the festival schedule.",
      "Register on your own or with your team. If you're coming solo, tell us what you do and we'll help you find a team.",
    ]
  ),
  firstName: createTextControl("What is your first name?"),
  lastName: createTextControl("What is your last name?"),
  email: createEmailControl("What email address can we reach you at?"),
  phoneNumber: createPhoneControl("What's the best phone number to reach you?"),
  basedIn: createTextControl("Where are you based?", "e.g., Edmonton, AB"),
  jam: createRadioControl("Which jam do you want to join?", ctjamJamOptions),
  team: createRadioControl("Are you registering a team or coming solo?", ctjamTeamOptions),
  teamName: createTextControl("Team name", "( optional )", true),
  teammates: createTextareaControl(
    "Who else is on your team?",
    "Names and email addresses, one per line ( optional )",
    true
  ),
  roles: createCheckboxControl("What do you do? (select all that apply)", ctjamRoleOptions),
  experience: createRadioControl("How much game jam experience do you have?", ctjamExperienceOptions),
  engine: createTextControl("Which engine or tools will you use?", "e.g., Unity, Godot, Unreal, GameMaker ( optional )", true),
  portfolioUrl: createUrlControl("Link to your portfolio, itch.io page or past games", "( optional )", true),
  accessibilityNeeds: createTextareaControl(
    "Anything we should know to make the jam accessible for you?",
    "( optional )",
    true
  ),
  additionalInfo: createTextareaControl("Anything else you'd like us to know?", "( optional )", true),
  acceptTerms: createAcceptControl(),
};

formControls[formSlugs.fashionPhotographerFebruary2027] = {
  intro: createIntroControl(
    "Photograph the C-Tribe x Fashion Show, February 25, 2027, Edmonton",
    [
      "We're selecting photographers for our standalone runway show on Thursday, February 25, 2027 in Edmonton.",
      "Tell us about your work, what you'd cover on the night and your terms. We review every application and reply by email.",
    ]
  ),
  firstName: createTextControl("What is your first name?"),
  lastName: createTextControl("What is your last name?"),
  email: createEmailControl("What email address can we reach you at?"),
  phoneNumber: createPhoneControl("What's the best phone number to reach you?"),
  basedIn: createTextControl("Where are you based?", "e.g., Edmonton, AB"),
  businessName: createTextControl("Business or studio name", "( optional )", true),
  portfolioUrl: createUrlControl("Link to your portfolio or website"),
  instagramHandle: { ...createTextControl("Instagram handle", "( optional )", true), example: "@yourhandle" },
  fullGalleryUrl: createUrlControl(
    "Link to a full gallery from a runway show or gala you photographed",
    "The whole set, not just highlights"
  ),
  availability: createRadioControl(
    "Are you available on Thursday, February 25, 2027, from 6:30 PM (VIP+ dinner) to the end of the post-show mixer?",
    photographerFebAvailabilityOptions
  ),
  availabilityNotes: createTextareaControl(
    "If only part of the evening, which hours?",
    "( optional )",
    true
  ),
  coverage: createCheckboxControl(
    "What could you cover? (select all that apply)",
    photographerFebCoverageOptions
  ),
  runwayExperience: createTextareaControl(
    "Have you photographed a runway show before? Which shows?"
  ),
  secondShooter: createRadioControl(
    "Would you bring a second shooter or assistant?",
    defaultRadioOptions
  ),
  gear: createTextareaControl(
    "What gear would you bring for low light and the runway, including backup?"
  ),
  arrangement: createRadioControl(
    "What are you looking for?",
    photographerFebArrangementOptions
  ),
  rate: createTextareaControl(
    "Your rate or package for the evening, and what's included",
    "Hours, number of edited images, anything else. Skip if you chose badge and credit only.",
    true
  ),
  turnaround: createTextareaControl(
    "How quickly would you deliver photos?",
    "e.g., same-night picks for social media, full edited gallery within 7 days"
  ),
  // Owner, 2026-10-26: C-Tribe holds the rights to event media exclusively;
  // photographers keep portfolio use. Full wording: /photo-video-release/ on www.
  usageRights: createRadioControl(
    "C-Tribe Society reserves the exclusive right to use, edit and publish all photos and media you capture at the event, in any format. Do you agree to give C-Tribe this right?",
    photographerFebRightsOptions,
    "You may still show a selection in your own portfolio, website and social media to promote your work. You may not sell or license event media to anyone else."
  ),
  hasLiabilityInsurance: createRadioControl(
    "Do you carry liability insurance?",
    defaultRadioOptions
  ),
  additionalInfo: createTextareaControl(
    "Anything else you'd like us to know?",
    "( optional )",
    true
  ),
  acceptTerms: createAcceptControl(
    'By hitting \'I accept\', I confirm the information above is accurate and agree that C-Tribe representatives may contact me by email or phone about this application and the event. I have authority to enter into the <a href="https://www.ctribefestival.com/terms/" class="text-blue-500" target="_blank" rel="noopener noreferrer">Terms of Service</a> and the <a href="https://www.ctribefestival.com/photo-video-release/" class="text-blue-500" target="_blank" rel="noopener noreferrer">Photo &amp; Video Release</a>, and have read the <a href="https://www.ctribefestival.com/privacy/" class="text-blue-500" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.'
  ),
};

const fashionModelSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  availableDays: z
    .array(z.enum(fashionModelAvailableDays))
    .min(1, { message: ERROR_MESSAGES.SelectOne() }),
  email: zodEmailValidator(""),
  phoneNumber: zodPhoneNumberValidator,
  socialMedialhandle: zodString("Social Media Handle", true),
  isRepresentedByAgency: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  agencyName: z.string().optional(),
  isPriorModelingExperience: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  // Hard requirement: models must be 18+. "No" cannot be submitted.
  isAgeAbove18: z
    .enum(defaultRadioOptions, { required_error: ERROR_MESSAGES.SelectOne() })
    .refine((value) => value === "Yes", { message: "Models must be 18 or older to apply." }),
  height: zodHeight(),
  bustMeasurement: zodMeasurement("bust or chest measurement", 24, 60),
  waistMeasurement: zodMeasurement("waist measurement", 18, 50),
  hipsMeasurement: zodMeasurement("hip measurement", 24, 60),
  inseam: zodMeasurement("inseam", 24, 40, true),
  clothingSize: zodString("Clothing Size"),
  shoeSizeType: z.enum(modelShoeSizeTypeOptions, { required_error: ERROR_MESSAGES.SelectOne() }),
  shoeSize: zodShoeSize(),
  hairColor: zodString("Hair Colour"),
  eyeColor: zodString("Eye Colour"),
  videoUrl: zodUrl("Video URL", true),
  acceptTerms: zodAcceptTerms,
};

const fashionProfessionalsSchema = {
  companyName: z.string().optional(),
  jobTitle: zodString("Job Title"),
  race: zodRaceOptionsSchema,
  gender: z
    .array(z.enum(genderOptions))
    .min(1, { message: ERROR_MESSAGES.SelectOne() }),
  city: zodString("City"),
  province: zodString("Province/State"),
  country: zodString("Country"),
  mainIndustry: z.enum(industryOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  additionalIndustries: z
    .array(z.enum(industryOptions))
    .max(3, ERROR_MESSAGES.MaxAllowed(3)),
  otherIndustry: z.string().optional(),
  websiteUrl: zodUrl("Website", true),
  attendanceGoals: zodString("Goals"),
  diversityForYou: zodString("Field"),
  acceptTerms: zodAcceptTerms,
};

// resume upload schema
const resumeUploadSchema = {
  resumeSummary: zodString("Resume Summary"),
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  professionalTitle: zodString("Professional Title"),
  email: zodEmailValidator(""),
  phoneNumber: zodPhoneNumberValidator,
  cityAndProvince: zodString("Field"),
  skills: zodString("Skills"),
  websiteUrl: zodUrl("Website", true),
  linkedInUrl: zodUrl("LinkedIn URL", true),
  educationalHistory: zodString("Education History"),
  workExperience: zodString("Work Experience"),
  resume: zodFileValidator("Resume", ["pdf", "doc"], 5, true),
  resumeReviewConsent: zodAcceptTerms,
};

// volunteer management schema
const volunteerSchema = {
  interests: z
    .array(z.nativeEnum(AreaOfInterest))
    .min(1, { message: ERROR_MESSAGES.SelectOne() }),
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  email: zodEmailValidator(""),
  phoneNumber: zodPhoneNumberValidator,
};

const mediaPressSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  email: zodEmailValidator(""),
  phoneNumber: zodPhoneNumberValidator,
  organizationName: zodString("Media Outlet or Organization"),
  role: zodString("Role or Title"),
  coverageFocus: zodString("Coverage Focus"),
};

// b2b matchmaking schema
const b2bSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  jobPosition: zodString("Job Position"),
  phoneNumber: zodPhoneNumberValidator,
  profilePhoto: zodFileValidator("Profile Photo", ["png"], 5),
  organizationName: zodString("Organization Name"),
  organizationType: z.enum(organizationTypeOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  companyWebsite: zodUrl("Company Website", false),
  organizationDescription: zodString("Organization Description"),
  organizationLogo: zodFileValidator("Organization Logo", ["pdf"], 5),
  activityAreas: zodActivityAreasOptionsSchema,
  intrestedServices: zodActivityAreasOptionsSchema,
  primaryGoal: z
    .array(z.enum(b2bPrimaryGoalOptions))
    .min(1, { message: ERROR_MESSAGES.SelectOne() }),
  otherGoals: z.string().optional(),
  address: zodString("Address"),
  city: zodString("City"),
  province: zodString("Province/State"),
  postalCode: zodString("Postal Code"),
  selectedSessions: z
    .array(z.enum(b2bSessionOptions))
    .min(1, { message: ERROR_MESSAGES.SelectOne() }),
  diversityForYou: zodString("Field"),
  acceptTerms: zodAcceptTerms,
};

// influencer schema
const influencerSchema = {
  firstName: zodString("First Name"),
  lastName: zodString("Last Name"),
  urls: zodString("URLs"),
  primaryContentFocus: zodString("Field"),
  email: zodEmailValidator(""),
  phoneNumber: zodPhoneNumberValidator,
  aim: zodString("Field"),
  diversityForYou: zodString("Field"),
  acceptTerms: zodAcceptTerms,
};

const fashionModelNovember2026Schema = {
  firstName: fashionModelSchema.firstName,
  lastName: fashionModelSchema.lastName,
  isAvailableFebruary25: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  ...Object.fromEntries(
    Object.entries(fashionModelSchema).filter(
      ([key]) => !["firstName", "lastName", "availableDays"].includes(key)
    )
  ),
};

const fashionDesignerFebruary2027Schema = {
  ...Object.fromEntries(
    Object.entries(fashionDesignerSchema).filter(
      ([key]) => key !== "acceptTerms"
    )
  ),
  isAvailableFebruary25: z.enum(defaultRadioOptions, {
    required_error: ERROR_MESSAGES.SelectOne(),
  }),
  acceptTerms: zodAcceptTerms,
};

const schemas = {
  // tech
  [formSlugs.techPitchElevateHer]: xTechPitchElevateHerSchema,
  [formSlugs.techPitchCBlack]: xTechPitchCBlackSchema,
  [formSlugs.techElevate]: xTechElevateApplicationSchema,
  [formSlugs.techSpeaker]: xTechSpeakerSignUpSchema,
  [formSlugs.techInvestor]: xTechStartupSignupSchema,
  [formSlugs.techEngineers]: xTechEngineerSignUpSchema,

  [formSlugs.techExecLuncheonGeneralAdmission2k25]: createZodSchema(
    techExecLuncheonFormControls
  ),
  [formSlugs.techCBlackSemiFinals]: createZodSchema(
    cBlackTechPitchSemiFinalsFormControls
  ),
  [formSlugs.techCElevateHerSemiFinals]: createZodSchema(
    cElevateHerTechPitchSemiFinalsFormControls
  ),
  [formSlugs.techStartupAlpha]: {
    ...createZodSchema(startupAlphaControls),
    ...startupAlphaSchemaOverrides,
  },

  // gaming
  [formSlugs.gamingEsportOverwatch]: xGamingPitchQuestSchema,
  [formSlugs.gamingSpeaker]: xGamingSpeakerSignUpSchema,
  [formSlugs.gamingDeveloper]: xGamingDeveloperSignUpSchema,
  [formSlugs.gamingPitchQuest]: gamingPitchQuestSchema,
  [formSlugs.gamingPlayTestZone]: gamingPlayTestZoneSchema,

  // fashion
  [formSlugs.fashionDesigner]: fashionDesignerSchema,
  [formSlugs.fashionBuyer]: fashionBuyerSchema,
  [formSlugs.fashionStylist]: fashionStylistSchema,
  [formSlugs.fashionMua]: fashionMuaSchema,
  [formSlugs.fashionModel]: fashionModelSchema,
  [formSlugs.fashionModelNovember2026]: fashionModelNovember2026Schema,
  [formSlugs.fashionDesignerFebruary2027]: fashionDesignerFebruary2027Schema,
  [formSlugs.fashionProfessionals]: fashionProfessionalsSchema,
  [formSlugs.fashionExhibit]: createZodSchema(formControls[formSlugs.fashionExhibit]),
  [formSlugs.fashionPartner]: createZodSchema(formControls[formSlugs.fashionPartner]),

  // general forms
  [formSlugs.vendor]: vendorSchema,
  [formSlugs.villageInterest]: villageInterestSchema,
  [formSlugs.resumeUpload]: resumeUploadSchema,
  [formSlugs.volunteer]: volunteerSchema,
  [formSlugs.mediaPress]: mediaPressSchema,
  [formSlugs.b2b]: b2bSchema,
  [formSlugs.influencer]: influencerSchema,
  [formSlugs.generalSpeaker]: createZodSchema(generalSpeakerFormControls),

  // music
  [formSlugs.musicArtist]: createZodSchema(musicArtistFormControls),
  [formSlugs.musicPublisher]: createZodSchema(musicPublisherFormControls),
  [formSlugs.musicPublisherPublisher]: createZodSchema(
    musicPublisherPublisherFromControls
  ),

  [formSlugs.diversityPass]: createZodSchema(diversityPassFormControls),

  // photography
  [formSlugs.photographer]: createZodSchema(photographerFormControls),

  // vip dinner
  [formSlugs.vipDinner]: createZodSchema(vipDinnerFormControls),

  [formSlugs.innovationZone]: createZodSchema(innovationZoneFormControls),

  // cross-vertical forms
  [formSlugs.partnership]: createZodSchema(formControls[formSlugs.partnership]),
  [formSlugs.film]: createZodSchema(formControls[formSlugs.film]),
  [formSlugs.filmSubmission]: filmSubmissionSchema,
  [formSlugs.filmPitch]: filmPitchSchema,
  [formSlugs.filmIndustry]: filmIndustrySchema,
  [formSlugs.artist]: createZodSchema(formControls[formSlugs.artist]),
  [formSlugs.health]: createZodSchema(formControls[formSlugs.health]),
  [formSlugs.videographer]: createZodSchema(formControls[formSlugs.videographer]),
  [formSlugs.gamingCtjam]: createZodSchema(formControls[formSlugs.gamingCtjam]),
  [formSlugs.fashionPhotographerFebruary2027]: createZodSchema(
    formControls[formSlugs.fashionPhotographerFebruary2027]
  ),
};


// Everyone C-Tribe would feature in a lineup is asked for a self-reported
// audience estimate (see audience-reach.ts).
export const PLATFORMED_FORM_SLUGS = [
  formSlugs.techSpeaker, formSlugs.gamingSpeaker, formSlugs.generalSpeaker,
  formSlugs.techPitchCBlack, formSlugs.techPitchElevateHer, formSlugs.gamingPitchQuest,
  formSlugs.fashionDesigner, formSlugs.fashionDesignerFebruary2027,
  formSlugs.fashionModel, formSlugs.fashionModelNovember2026,
  formSlugs.musicArtist, formSlugs.artist,
] as const;
for (const slug of PLATFORMED_FORM_SLUGS) {
  formControls[slug] = withAudienceReach(formControls[slug], audienceReachControls);
  (schemas as Record<string, ZodRawShape>)[slug] = withAudienceReach(
    (schemas as Record<string, ZodRawShape>)[slug],
    audienceReachSchema
  );
}

export { schemas, formControls, formNames };
